import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactModal from "react-modal";
import Modal from "react-modal";
import { getApi, postApi, putApi } from "../Services/apiService";
import { toast } from "react-toastify";
import "../popups.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  deriveSpouseRelationFromPrimary,
  getFamilyLabels,
} from "../Utils/familyRelations";
import DropdownSelect from "../Components/FamilyTree/DropdownSelect";
import dayjs from "dayjs";
import CustomDatePicker from "../Components/AdminComponents/Date/CustomDatePicker";
import DeleteConfirmationModal from "./DeleteConfirmation";
import RootHeadModal from "./RootHeadModal";
import {
  isHeadOfLineMember,
  getHeadOfLineLabels,
  getHeadOfLineRelation,
  getSpouseHeadOfLineRelation,
  calculateHeadOfLineRelation,
  calculateHeadOfLineFromFamilyLabels,
} from "../Utils/headOfLineUtils";
import CircularProgress from "@mui/material/CircularProgress";
import { combinePhoneNumber, extractPhoneNumber } from "../Utils/phone";
import { isDateComplete } from "../Utils/dateAndTime";
// import { sortByKey } from "../Utils/sortByKey";
import {
  validatePrimaryMemberDates,
  validateSpouseDates,
  validateAdditionalSpouseDates,
  areAllDateValidationsPassing,
  validateChildBirthDate,
  validateCrossSpouseDates,
} from "../Utils/dateValidationUtils";
import { convertRelationByGender } from "../Utils/relations";
import { MainMemberSection } from "./AddMemberComponents/MainMemberSection";
import { MainSpouseSection } from "./AddMemberComponents/MainSpouseSection";
import { AdditionalSpouseSection } from "./AddMemberComponents/AdditionalSpouseSection";
import { sortedStates, sortedGenderOptions, sortedMaritalStatusOptions, sortedLivingDeceasedOptions, multipleBirthsOptions, sortedCellTypeOptions, sortedDivorceOptions, isInRelationshipMaritalStatus } from "../Utils/family-tree/dropdown";
import {
  calculateAge,
  calcMarriedYears,
  formatDateForInput,
  isValidDate,
} from "../Utils/family-tree/dateAndTime";
import { handleFormSubmit } from "../Utils/family-tree/handleFormSubmit";
import {
  clearFamilyTreeIdsFromStorage,
  getFamilyTreeIdFromStorage,
} from "../Utils/headOfLineRole";
import { checkEmailExistsUtils } from "../Utils/family-tree/email";
import { validateDatesUtils } from "../Utils/family-tree/validateDates";
import { formatNameWithSuffix } from "../Utils/name";
import { limitNationalPhoneDigits } from "../Utils/family-tree/addMemberFieldLimits";

const SPOUSE_EMAIL_KNOWN_DOMAIN_LIST = [
  "@aol.com",
  "@gmail.com",
  "@hotmail.com",
  "@icloud.com",
  "@mail.com",
  "@outlook.com",
  "@protonmail.com",
  "@yahoo.com",
];
const SPOUSE_EMAIL_KNOWN_DOMAINS = new Set(
  SPOUSE_EMAIL_KNOWN_DOMAIN_LIST.map((d) => d.toLowerCase())
);

/** Prefill spouse email_domain / email_custom_domain for the domain dropdown (same behavior as descendant). */
function getSpouseEmailAuxiliaryFromAddress(email) {
  const raw = (email || "").trim();
  if (!raw || !raw.includes("@")) {
    return { email_domain: "", email_custom_domain: "" };
  }
  const at = raw.indexOf("@");
  const domainPart = raw.slice(at + 1).trim().toLowerCase();
  if (!domainPart) {
    return { email_domain: "", email_custom_domain: "" };
  }
  const atDomain = `@${domainPart}`;
  if (SPOUSE_EMAIL_KNOWN_DOMAINS.has(atDomain)) {
    return { email_domain: atDomain, email_custom_domain: "" };
  }
  return { email_domain: "other", email_custom_domain: domainPart };
}

// Set the app element for accessibility
if (process.env.NODE_ENV !== "test") {
  ReactModal.setAppElement("#root");
}

const AddMemberModal = ({
  isOpen,
  onClose,
  onNext,
  rootHeadData,
  parentMember,
  familyTreeId,
  isEditMode = false,
  editMemberData = null,
}) => {

  // console.log("editMemberData", editMemberData);

  // Determine labels based on generation
  const isRootGeneration = !parentMember;
  const parentGeneration = parentMember?.generation || 0;
  const rootHeadGeneration = rootHeadData?.generationLevel || 0;
  const rootHeadGenerationName = rootHeadData?.selectedHeadName;
  // console.log("rootHeadGeneration", rootHeadGeneration);
 
  // Use rootHeadGeneration if available, otherwise fallback to parentGeneration
  const effectiveGeneration = rootHeadGeneration || parentGeneration;

  const [primaryLabelName, setPrimaryLabelName] = useState("");
  const [spouseLabelName, setSpouseLabelName] = useState("");
  const [primaryRelationName, setPrimaryRelationName] = useState("");
  const [spouseRelationName, setSpouseRelationName] = useState("");
  // console.log("effective generation : ",effectiveGeneration);


  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [showColorChangeModal, setShowColorChangeModal] = useState(false);
  const [selectedProfileColor, setSelectedProfileColor] = useState({});
  const [rootHeadData2, setRootHeadData2] = useState({});
  const [phoneValidationError, setPhoneValidationError] = useState(false);
  const [relationToHeadOfLine, setRelationToHeadOfLine] = useState("");
  
  // State to store fetched member data (for edit mode) - declare before use
  const [fetchedMemberData, setFetchedMemberData] = useState(null);
  
  // Calculate currentGeneration - in edit mode, use editMemberData's generation_level
  const currentGeneration = useMemo(() => {
    if (isEditMode && editMemberData) {
      // In edit mode, use the member's generation_level directly
      const memberGen = editMemberData.generation_level || editMemberData.generation || 
                       fetchedMemberData?.generation_level || fetchedMemberData?.generation;
      return memberGen || (isRootGeneration ? 1 : parentGeneration + 1);
    }
    // In add mode, calculate from parent
    return isRootGeneration ? 1 : parentGeneration + 1;
  }, [isEditMode, editMemberData?.generation_level, editMemberData?.generation, fetchedMemberData?.generation_level, fetchedMemberData?.generation, isRootGeneration, parentGeneration]);
  
  // State to store fetched children data
  const [parentChildrenData, setParentChildrenData] = useState([]);
  
  // Fetch children data when modal opens (both add and edit mode)
  // This fetches siblings for the parent member
  useEffect(() => {
    const fetchChildrenData = async () => {
      // Determine parent ID - in edit mode, get from editMemberData, otherwise from parentMember
      let parentId = null;
      let parentGen = 0;
      
      if (isEditMode && editMemberData) {
        // In edit mode, get parent ID from editMemberData or fetchedMemberData
        const memberData = fetchedMemberData || editMemberData.userData || editMemberData;
        parentId = memberData.related_member_id || memberData.parent_id || memberData.parentId || editMemberData.related_member_id || editMemberData.parent_id || editMemberData.parentId;
        parentGen = (memberData.generation_level || memberData.generation || editMemberData.generation_level || editMemberData.generation || 2) - 1;
        
        console.log("🟡 [AddMemberModal] Edit mode - Fetching siblings:", {
          editMemberId: editMemberData.id,
          editMemberName: editMemberData.first_name || editMemberData.name,
          parentId: parentId,
          parentGeneration: parentGen,
          currentGeneration: currentGeneration,
          memberDataFields: {
            related_member_id: memberData.related_member_id,
            parent_id: memberData.parent_id,
            generation_level: memberData.generation_level
          }
        });
      } else if (!isEditMode && parentMember && parentMember.id) {
        // In add mode, use parentMember to fetch its children (siblings of the new child)
        parentId = parentMember.id;
        parentGen = parentGeneration;
        
        console.log("🟢 [AddMemberModal] Add mode - Fetching siblings for new child:", {
          parentId: parentId,
          parentName: parentMember.first_name || parentMember.name,
          parentGeneration: parentGen,
          currentGeneration: currentGeneration
        });
      }
      
      if (isOpen && parentId) {
        // Check if children data is already available
        const existingChildren = parentMember?.children || [];
        
        console.log("🟢 [AddMemberModal] Modal opened - Fetching children/siblings:", {
          mode: isEditMode ? "EDIT" : "ADD",
          parentId: parentId,
          parentGeneration: parentGen,
          currentGeneration: currentGeneration,
          existingChildrenCount: existingChildren.length
        });
        
        // Always try to fetch from API to get the latest children data
        try {
          console.log("🔵 [AddMemberModal] Fetching children data for parent:", parentId);
          const response = await getApi(`/family-member-v2/${parentId}`);
          
          // Log the COMPLETE response structure to see all available paths
          console.log("🔵 [AddMemberModal] COMPLETE API RESPONSE STRUCTURE:", {
            fullResponse: response,
            responseData: response.data,
            responseDataKeys: response.data ? Object.keys(response.data) : [],
            hasChildren: !!response.data?.children,
            hasTree: !!response.data?.tree,
            childrenPath: "response.data?.children",
            treePath: "response.data?.tree",
            childrenCount: response.data?.children?.length || 0,
            treeStructure: response.data?.tree ? {
              treeLength: response.data.tree.length,
              firstTreeItem: response.data.tree[0],
              firstTreeItemKeys: response.data.tree[0] ? Object.keys(response.data.tree[0]) : [],
              firstTreeItemChildren: response.data.tree[0]?.children,
              firstTreeItemChildrenCount: response.data.tree[0]?.children?.length || 0
            } : null
          });
          
          // Try multiple paths to get children with full data
          let fetchedChildren = [];
          
          // Path 1: response.data.children (simplified)
          if (response.data?.children && response.data.children.length > 0) {
            console.log("📋 [AddMemberModal] Found children at response.data.children (simplified):", response.data.children);
            fetchedChildren = response.data.children;
          }
          
          // Path 2: response.data.tree[0].children (full data - like screenshot 3)
          if (response.data?.tree && response.data.tree[0]?.children && response.data.tree[0].children.length > 0) {
            console.log("📋 [AddMemberModal] Found children at response.data.tree[0].children (FULL DATA):", response.data.tree[0].children);
            fetchedChildren = response.data.tree[0].children;
          }
          
          // If we have simplified children, fetch full data for each child
          if (fetchedChildren.length > 0 && fetchedChildren[0] && !fetchedChildren[0].user_id) {
            console.log("⚠️ [AddMemberModal] Children have simplified data, fetching full data for each child...");
            const fullChildrenData = await Promise.all(
              fetchedChildren.map(async (child) => {
                try {
                  const childResponse = await getApi(`/family-member-v2/${child.id}`);
                  console.log(`📋 [AddMemberModal] Fetched full data for child ${child.id}:`, childResponse.data);
                  return childResponse.data || child;
                } catch (error) {
                  console.error(`❌ [AddMemberModal] Error fetching full data for child ${child.id}:`, error);
                  return child;
                }
              })
            );
            fetchedChildren = fullChildrenData;
          }
          
          // Log each child with ALL its data
          fetchedChildren.forEach((child, index) => {
            console.log(`📋 [AddMemberModal] Child ${index} - COMPLETE API DATA (all fields from getApi):`, {
              index: index,
              allKeys: Object.keys(child),
              completeChildObject: child,
              // Show user_id specifically
              user_id: child.user_id,
              id: child.id,
              first_name: child.first_name,
              last_name: child.last_name,
              full_name: child.full_name,
              name: child.name,
              suffix: child.suffix
            });
          });
          
          // Use fetched children if available, otherwise use existing
          setParentChildrenData(fetchedChildren.length > 0 ? fetchedChildren : existingChildren);
        } catch (error) {
          console.error("❌ [AddMemberModal] Error fetching children data:", error);
          // Fallback to existing children data if API call fails
          setParentChildrenData(existingChildren);
        }
      } else if (!isOpen) {
        // Clear children data when modal closes
        setParentChildrenData([]);
      }
    };
    
    fetchChildrenData();
  }, [isOpen, isEditMode, parentMember?.id, editMemberData?.id, editMemberData?.related_member_id, fetchedMemberData?.related_member_id, currentGeneration, parentGeneration]);
  const [imageValidationError, setImageValidationError] = useState(false);
  const [spouseImageValidationError, setSpouseImageValidationError] = useState(false);
  const [isLoadingMemberData, setIsLoadingMemberData] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Consolidated spouse state - single array for all spouses
  const [spouses, setSpouses] = useState([
    {
      id: "main-spouse",
      firstName: "",
      middleName: "",
      lastName: "",
      suffix: "",
      maidenName: "",
      nickName: "",
      gender: "",
      dob: "",
      stateOfBirth: "",
      currentStateResiding: "",
      aliveStatus: "",
      dod: "",
      ageOfDeath: "",
      currentAge: "",
      maritalStatus: "",
      weddingAnniversary: "",
      note: "",
      relation: "",
      email: "",
      email_domain: "",
      email_custom_domain: "",
      phone: "",
      countryCode: "",
      phone_type: "",
    },
  ]);
  const [additionalSpouses, setAdditionalSpouses] = useState([]);
  const [openSpouseSections, setOpenSpouseSections] = useState(["main-spouse"]);

  // Date validation errors state
  const [dateValidationErrors, setDateValidationErrors] = useState({
    primary: {
      dob: "",
      dod: "",
      weddingAnniversary: "",
    },
    spouse: {
      dob: "",
      dod: "",
      weddingAnniversary: "",
    },
  });

  // Field validation errors state
  const [fieldValidationErrors, setFieldValidationErrors] = useState({
    maritalStatus: "",
    email: "",
    aliveStatus: "",
    gender: "",
    stateOfBirth: ""
  });

  // Spouse validation errors state
  const [spouseValidationErrors, setSpouseValidationErrors] = useState({
    divorce: "",
    email: "",
    aliveStatus: "",
    firstName: "",
    lastName: "",
    image: "",
    gender: "",
  });

  const [additionalSpouseErrors, setAdditionalSpouseErrors] = useState({});

  // Additional spouse image validation errors (per spouse)
  const [additionalSpouseImageErrors, setAdditionalSpouseImageErrors] = useState({});

  // Track which fields have been touched/interacted with
  const [touchedFields, setTouchedFields] = useState({
    primary: {
      dob: false,
      dod: false,
      weddingAnniversary: false,
      maritalStatus: false,
      email: false,
      aliveStatus: false,
    },
    spouse: {
      dob: false,
      dod: false,
      weddingAnniversary: false,
      divorce: false,
      email: false,
      aliveStatus: false,
      firstName: false,
      lastName: false,
    },
  });

  // Track if form has been submitted (to show all errors)
  const [formSubmitted, setFormSubmitted] = useState(false);

  // console.log("Generation Mapping:", {
  //   isRootGeneration,
  //   parentGeneration,
  //   currentGeneration,
  //   parentMember,
  //   expectedRelation: getHeadOfLineRelation(
  //     rootHeadGenerationName,
  //     currentGeneration
  //   ),
  // });

  // Open delete modal for different item types
  const openDeleteModal = (item, itemType = "profile") => {
    setSelectedItem({ ...item, type: itemType });
    setShowDeleteModal(true);
  };

  // Handle modal close
  const handleDeleteModalClose = (success = false) => {
    setShowDeleteModal(false);
    if (success) {
      // Refresh your data or update state
      console.log("Item deleted successfully");
    }
  };

  // Set dynamic labels based on generation level
  let primaryLabel, spouseLabel, primaryRelation, spouseRelation;
  // debugger
  if (isRootGeneration) {
    // Normalize "GrandFather" to "Grandfather"
    const normalizedRootHeadName = rootHeadGenerationName === "GrandFather" ? "Grandfather" : rootHeadGenerationName;
    primaryLabel = normalizedRootHeadName;
    spouseLabel =
      normalizedRootHeadName == "Grandfather" ? "Grandmother" : "Grandfather";

    primaryRelation = "Self";
    spouseRelation = `${spouseLabel} - Spouse`;
  } else if (effectiveGeneration === 1) {
    // Normalize "GrandFather" to "Grandfather"
    primaryLabel = rootHeadGenerationName === "GrandFather" ? "Grandfather" : rootHeadGenerationName;

  } else if (effectiveGeneration === 2) {
    // Normalize "GrandFather" to "Grandfather"
    primaryLabel = rootHeadGenerationName === "GrandFather" ? "Grandfather" : rootHeadGenerationName;

  } else if (effectiveGeneration === 3) {
    // Normalize "GrandFather" to "Grandfather"
    primaryLabel = rootHeadGenerationName === "GrandFather" ? "Grandfather" : rootHeadGenerationName;

  } else {
    // Normalize "GrandFather" to "Grandfather"
    primaryLabel = rootHeadGenerationName === "GrandFather" ? "Grandfather" : rootHeadGenerationName;

  }
  const [selectedState, setSelectedState] = useState("");
  const [openSection, setOpenSection] = useState("primary");
  const [loading, setLoading] = useState(false);
  const [grandfatherData, setGrandfatherData] = useState({
    firstName: "",
    middleName: "",
    specialName: "",
    lastName: "",
    suffix: "",
    maidenName: "",
    nickName: "",
    // gender: "",
    gender: rootHeadData?.selectedHeadName == "Son" ? 1 : 2,
    dob: "",
    stateOfBirth: "",
    currentStateResiding: "",
    aliveStatus: "",
    dod: "",
    ageOfDeath: "",
    // 2. ADD currentAge TO STATE
    currentAge: "",
    maritalStatus: "",
    weddingAnniversary: "",
    note: "",
    // relation: primaryRelation,
    email: "",
    phone: "",
    countryCode: "",
    relation_to_head_of_line: "",
    bio_descendant_parent: "",
    bio_non_descendant_parent: "",
    step_parent: "",
    adopted_parent: "",
    phone_type: "",
    multiple_births_type: "",
    multiple_birth_with_member: "",
    multiple_birth_order: "",
    multiple_birth_details: [],
  });

  // Effect to pre-select Multiple Births fields in edit mode
  useEffect(() => {
    if (isEditMode && isOpen && parentChildrenData.length > 0) {
      const memberData = fetchedMemberData || editMemberData?.userData || editMemberData;
      const multipleBirthMemberUserId = memberData?.multiple_birth_member_user_id || editMemberData?.multiple_birth_member_user_id;
      const multipleBirthOrderNumber = memberData?.multiple_birth_order_number || editMemberData?.multiple_birth_order_number;
      const multipleBirthsType = memberData?.multiple_births_type || editMemberData?.multiple_births_type;

      // Only proceed if we have the data and haven't already set the multiple_birth_with_member
      if (multipleBirthMemberUserId && multipleBirthsType && !grandfatherData?.multiple_birth_with_member) {
        // Find the sibling that matches the user_id
        const matchingSibling = parentChildrenData.find(
          child => {
            const childUserId = child.user_id || child.userId || child.User?.id || child.User?.user_id || "";
            return childUserId?.toString() === multipleBirthMemberUserId?.toString();
          }
        );

        if (matchingSibling) {
          console.log("🟢 [AddMemberModal] Pre-selecting Multiple Births fields in edit mode:", {
            multipleBirthMemberUserId: multipleBirthMemberUserId,
            matchingSiblingId: matchingSibling.id,
            matchingSiblingName: matchingSibling.first_name || matchingSibling.name?.split(' ')[0] || "sibling",
            multipleBirthOrderNumber: multipleBirthOrderNumber,
            multipleBirthsType: multipleBirthsType,
            siblingData: matchingSibling
          });

          // Auto-fill DOB from sibling if not already set in form
          let dobValue = grandfatherData?.dob || "";
          if (matchingSibling.dob && !grandfatherData?.dob) {
            // Convert sibling DOB to YYYY/MM/DD format (CustomDatePicker format)
            let siblingDOBFormatted = "";
            if (typeof matchingSibling.dob === 'string' && matchingSibling.dob.includes('/')) {
              const parts = matchingSibling.dob.split('/');
              if (parts.length === 3) {
                // Check if it's MM/DD/YYYY or YYYY/MM/DD
                if (parts[0].length === 4) {
                  // Already YYYY/MM/DD
                  siblingDOBFormatted = matchingSibling.dob;
                } else {
                  // MM/DD/YYYY format
                  siblingDOBFormatted = `${parts[2]}/${parts[0]}/${parts[1]}`;
                }
              } else {
                siblingDOBFormatted = matchingSibling.dob;
              }
            } else if (matchingSibling.dob) {
              const timestamp = typeof matchingSibling.dob === 'string' ? parseInt(matchingSibling.dob) : matchingSibling.dob;
              if (!isNaN(timestamp) && timestamp !== 0) {
                const date = new Date(timestamp);
                if (!isNaN(date.getTime())) {
                  const year = date.getUTCFullYear();
                  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(date.getUTCDate()).padStart(2, '0');
                  siblingDOBFormatted = `${year}/${month}/${day}`;
                }
              }
            }
            if (siblingDOBFormatted) {
              dobValue = siblingDOBFormatted;
            }
          }

          // Get parent fields from sibling's User object
          const siblingUser = matchingSibling.User || {};
          
          // Update grandfatherData with the matched sibling's id, order, DOB, and parent fields
          setGrandfatherData((prev) => ({
            ...prev,
            multiple_births_type: multipleBirthsType,
            multiple_birth_with_member: matchingSibling.id?.toString() || "",
            multiple_birth_order: multipleBirthOrderNumber?.toString() || "",
            // Auto-fill DOB if not already set
            dob: dobValue || prev.dob,
            // Auto-fill parent fields from sibling
            bio_descendant_parent: siblingUser.bio_descendant_parent || prev.bio_descendant_parent || "",
            bio_non_descendant_parent: siblingUser.bio_non_descendant_parent || prev.bio_non_descendant_parent || "",
            step_parent: siblingUser.step_parent || prev.step_parent || "",
            adopted_parent: siblingUser.adopted_parent || prev.adopted_parent || "",
          }));
        } else {
          console.warn("⚠️ [AddMemberModal] Could not find sibling matching user_id:", {
            multipleBirthMemberUserId: multipleBirthMemberUserId,
            parentChildrenCount: parentChildrenData.length,
            parentChildrenUserIds: parentChildrenData.map(c => ({
              id: c.id,
              user_id: c.user_id || c.userId || c.User?.id || c.User?.user_id || "NOT FOUND"
            }))
          });
        }
      }
    }
  }, [isEditMode, isOpen, parentChildrenData, fetchedMemberData, editMemberData]);

  const [phoneError, setPhoneError] = useState("");

  // 2. Add state for file upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);
  const emailCheckTimeoutRef = useRef(null);

  // State for multiple image uploads
  const [uploadedImages, setUploadedImages] = useState([]);

  // grandmotherData is now derived from the first spouse in the spouses array
  const grandmotherData = spouses[0] || {
    firstName: "",
    middleName: "",
    specialName: "",
    lastName: "",
    suffix: "",
    maidenName: "",
    nickName: "",
    gender: rootHeadData?.selectedHeadName?.toLowerCase() == "son" ? 1 : 2 || "",
    dob: "",
    stateOfBirth: "",
    currentStateResiding: "",
    aliveStatus: "",
    dod: "",
    ageOfDeath: "",
    currentAge: "",
    maritalStatus: "",
    weddingAnniversary: "",
    note: "",
    relation: spouseRelation,
    email: "",
    email_domain: "",
    email_custom_domain: "",
    phone: "",
    countryCode: "",
    phone_type: "",
    wedding_anniversary: ""
  };


  // Fetch member data when modal opens in edit mode
  useEffect(() => {
    const fetchMemberDataForEdit = async () => {
      if (isOpen && isEditMode && editMemberData?.id && !editMemberData?.userData) {
        setIsLoadingMemberData(true);
        try {
          console.log("Fetching member data for edit...");
          const response = await getApi(`/family-member-v2/${editMemberData.id}`);
          console.log("Fetched member data:", response.data);
          setFetchedMemberData(response.data);

          // Set isAdmin from response data
          setIsAdmin(response.data?.isAdmin || false);

          // Update editMemberData with userData
          if (response.data) {
            // This will trigger the form prefill useEffect
            editMemberData.userData = response.data;
          }
        } catch (error) {
          console.error('Error fetching member data for edit:', error);
          toast.error("Failed to load member data. Please try again.");
        } finally {
          setIsLoadingMemberData(false);
        }
      } else if (isOpen && isEditMode && editMemberData?.userData) {
        // If userData already exists, use it
        setFetchedMemberData(editMemberData.userData);
        // Set isAdmin from existing userData
        setIsAdmin(editMemberData.userData?.isAdmin || false);
      }
    };

    fetchMemberDataForEdit();
  }, [isOpen, isEditMode, editMemberData?.id]);

  // Reset form when modal is opened
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      // ... (Rest of your existing useEffect code)

      let primaryRel, spouseRel;
      primaryRel = editMemberData?.relation || "";
      spouseRel = editMemberData?.spouse_relation || "";

      if (isRootGeneration) {
        //primaryRel = "Grandfather - Root Head of Family";
        // spouseRel = "Grandmother - Spouse";
      } else if (effectiveGeneration === 1) {
        //primaryRel = "Son";
        // spouseRel = "Daughter";
      } else if (effectiveGeneration === 2) {
        //primaryRel = "Grandson";
        // spouseRel = "Granddaughter";
      } else if (effectiveGeneration === 3) {
        //primaryRel = "Great-Grandson";
        // spouseRel = "Great-Granddaughter";
      } else {
        //primaryRel = "Child";
        //spouseRel = "Spouse";
      }

      if (isEditMode && editMemberData) {
        // ... (Existing edit mode logic)

        const spouseDOB = formatDateForInput(editMemberData?.spouseDob);
        const spouseDOD = formatDateForInput(editMemberData?.spouseDateOfDeath);

        const initialSpouseAgeOfDeath =
          editMemberData.spouseAliveStatus?.toString() === "2"
            ? calculateAge(spouseDOB, spouseDOD)
            : "";
        const initialSpouseCurrentAge =
          editMemberData.spouseAliveStatus?.toString() === "1"
            ? calculateAge(spouseDOB)
            : "";

        const mainMemberDOB = formatDateForInput(
          editMemberData?.userData?.dob || editMemberData?.dob || editMemberData?.dateOfBirth || ""
        );
        const mainMemberDOD = formatDateForInput(
          editMemberData?.userData?.date_of_death || editMemberData?.date_of_death || editMemberData?.dateOfDeath || editMemberData?.dod || ""
        );

        // Get alive status for age calculations
        const aliveStatus = editMemberData?.userData?.present_or_deceased || editMemberData?.present_or_deceased || editMemberData?.aliveStatus || "";

        // Pre-calculate initial ages
        const initialMainAgeOfDeath = aliveStatus?.toString() === "2" && mainMemberDOB && mainMemberDOD
          ? calculateAge(mainMemberDOB, mainMemberDOD)
          : "";
        const initialMainCurrentAge = aliveStatus?.toString() === "1" && mainMemberDOB
          ? calculateAge(mainMemberDOB)
          : "";

        // Create form data with proper field mapping and fallbacks
        console.log("=== CONVERTING GRANDFATHER DATES ===");

        const memberData = fetchedMemberData || editMemberData?.userData;
        // Set isAdmin from memberData if available
        if (memberData?.isAdmin !== undefined) {
          setIsAdmin(memberData.isAdmin);
        }

        const loadedMaritalStatus =
          memberData?.marital_status ||
          editMemberData?.marital_status ||
          editMemberData?.maritalStatus;
        const loadedMaritalStatusStr = loadedMaritalStatus
          ? String(loadedMaritalStatus)
          : "";
        const isInRelationshipOnLoad = isInRelationshipMaritalStatus(
          loadedMaritalStatusStr
        );

        const grandfatherData = {
          firstName: memberData?.first_name || editMemberData?.first_name || editMemberData?.firstName || "",
          middleName: memberData?.middle_name || editMemberData?.middle_name || editMemberData?.middleName || "",
          lastName: memberData?.last_name || editMemberData?.last_name || editMemberData?.lastName || "",
          suffix: memberData?.suffix || editMemberData?.suffix || "",
          maidenName: memberData?.maiden_name || editMemberData?.maiden_name || editMemberData?.maidenName || "",
          nickName: memberData?.nick_name || editMemberData?.nick_name || editMemberData?.nickName || "",
          gender: memberData?.gender || editMemberData?.gender ? String(memberData?.gender || editMemberData?.gender) : "",
          dob: mainMemberDOB,
          stateOfBirth: memberData?.state_of_birth || editMemberData?.state_of_birth || editMemberData?.stateOfBirth || "",
          currentStateResiding: memberData?.current_state_residing || editMemberData?.current_state_residing || editMemberData?.currentStateResiding || "",
          aliveStatus: aliveStatus ? String(aliveStatus) : "",
          dod: mainMemberDOD,
          ageOfDeath: initialMainAgeOfDeath || memberData?.age_at_death || editMemberData?.age_at_death || editMemberData?.ageAtDeath || editMemberData?.ageOfDeath || "",
          currentAge: initialMainCurrentAge,
          maritalStatus: loadedMaritalStatusStr,
          weddingAnniversary: isInRelationshipOnLoad
            ? ""
            : formatDateForInput(
                memberData?.wedding_anniversary ||
                  editMemberData?.wedding_anniversary ||
                  editMemberData?.weddingAnniversary ||
                  ""
              ),
          note: memberData?.notes || editMemberData?.notes || editMemberData?.note || "",
          relation: memberData?.relation || editMemberData?.relation || primaryRel,
          // Clear email if Deceased (aliveStatus === "2")
          email: aliveStatus?.toString() === "2" ? "" : (memberData?.User?.email || editMemberData?.email || ""),
          phone: combinePhoneNumber(memberData?.User?.phone_number || editMemberData?.phone_number || editMemberData?.phone, memberData?.User?.country_code || editMemberData?.country_code || editMemberData?.countryCode) || "",
          countryCode: memberData?.User?.country_code || editMemberData?.country_code || editMemberData?.countryCode || "",
          phone_type: memberData?.User?.phone_type || editMemberData?.phone_type || editMemberData?.phoneType || editMemberData?.cell_type || "",
          relation_to_head_of_line: memberData?.relation_to_head_of_line || editMemberData?.relation_to_head_of_line || "",
          adopted_parent: memberData?.adopted_parent || memberData?.User?.adopted_parent || editMemberData?.adopted_parent || "",
          step_parent: memberData?.step_parent || memberData?.User?.step_parent || editMemberData?.step_parent || "",
          bio_descendant_parent: memberData?.bio_descendant_parent || memberData?.User?.bio_descendant_parent || editMemberData?.bio_descendant_parent || "",
          bio_non_descendant_parent: memberData?.bio_non_descendant_parent || memberData?.User?.bio_non_descendant_parent || editMemberData?.bio_non_descendant_parent || "",
          multiple_births_type: memberData?.multiple_births_type || editMemberData?.multiple_births_type || "",
          multiple_birth_with_member: "", // Will be set in useEffect after parentChildrenData is loaded
          multiple_birth_order: memberData?.multiple_birth_order_number || editMemberData?.multiple_birth_order_number || "",
          multiple_birth_details: (() => {
            // Parse multiple_birth_details from API response
            // It could be a JSON string, an array, or undefined
            const details = memberData?.multiple_birth_details || editMemberData?.multiple_birth_details;
            if (!details) return [];
            
            let parsedDetails = [];
            
            // If it's a string, parse it
            if (typeof details === 'string') {
              try {
                const parsed = JSON.parse(details);
                parsedDetails = Array.isArray(parsed) ? parsed : [];
              } catch (e) {
                console.warn('Failed to parse multiple_birth_details:', e);
                return [];
              }
            } else if (Array.isArray(details)) {
              parsedDetails = details;
            } else {
              return [];
            }
            
            // Normalize the details: ensure family_member_id and order are properly formatted
            // family_member_id should be a string for comparison with sibling.value
            // order should be a number or string (will be converted to string in dropdown)
            return parsedDetails.map(detail => ({
              ...detail,
              family_member_id: detail.family_member_id?.toString() || detail.family_member_id || "",
              order: detail.order !== undefined && detail.order !== null ? detail.order.toString() : ""
            }));
          })(),
        };


        // Get first spouse from all_spouses (from API) or fallback to editMemberData
        const firstSpouse = memberData?.all_spouses?.[0] || {};

        // Get spouse alive status for age calculations
        const spouseAliveStatus = firstSpouse?.spouse_present_or_deceased || editMemberData?.spouse_present_or_deceased || editMemberData?.spouseAliveStatus || "";

        const grandmotherData = {
          firstName: firstSpouse?.spouse_first_name || editMemberData?.spouse_first_name || editMemberData?.spouseFirstName || "",
          middleName: firstSpouse?.spouse_middle_name || editMemberData?.spouse_middle_name || editMemberData?.spouseMiddleName || "",
          specialName: firstSpouse?.spouse_special_name || editMemberData?.spouse_special_name || editMemberData?.spouseSpecialName || "",
          lastName: firstSpouse?.spouse_last_name || editMemberData?.spouse_last_name || editMemberData?.spouseLastName || "",
          suffix: firstSpouse?.spouse_suffix || editMemberData?.spouse_suffix || editMemberData?.spouseSuffix || "",
          maidenName: firstSpouse?.spouse_maiden_name || editMemberData?.spouse_maiden_name || editMemberData?.spouseMaidenName || "",
          nickName: firstSpouse?.spouse_nick_name || editMemberData?.spouse_nick_name || editMemberData?.spouseNickName || "",
          gender: firstSpouse?.spouse_gender || editMemberData?.spouse_gender || editMemberData?.spouseGender ? String(firstSpouse?.spouse_gender || editMemberData?.spouse_gender || editMemberData?.spouseGender) : "",
          dob: spouseDOB,
          stateOfBirth: firstSpouse?.spouse_state_of_birth || editMemberData?.spouse_state_of_birth || editMemberData?.spouseStateOfBirth || "",
          currentStateResiding: firstSpouse?.spouse_current_state_residing || editMemberData?.spouse_current_state_residing || editMemberData?.spouseCurrentStateResiding || "",
          aliveStatus: spouseAliveStatus ? String(spouseAliveStatus) : "",
          dod: spouseDOD,
          ageOfDeath: initialSpouseAgeOfDeath || firstSpouse?.spouse_age_at_death || editMemberData?.spouse_age_at_death || editMemberData?.spouseAgeAtDeath || "",
          currentAge: initialSpouseCurrentAge,
          maritalStatus: firstSpouse?.marital_status || editMemberData?.marital_status ? String(firstSpouse?.marital_status || editMemberData?.marital_status) : "",
          weddingAnniversary: isInRelationshipOnLoad
            ? ""
            : formatDateForInput(
                firstSpouse?.wedding_anniversary ||
                  editMemberData?.userData?.all_spouses?.[0]?.wedding_anniversary ||
                  editMemberData?.wedding_anniversary ||
                  editMemberData?.weddingAnniversary ||
                  ""
              ),
          note: firstSpouse?.notes || editMemberData?.notes || editMemberData?.note || "",
          relation: firstSpouse?.spouse_relation || editMemberData?.spouse_relation ||
            deriveSpouseRelationFromPrimary(editMemberData?.relation || primaryRel || spouseRel) ||
            editMemberData?.spouseRelation || "",
          // Clear email if Deceased (spouseAliveStatus === "2")
          email: spouseAliveStatus?.toString() === "2" ? "" : (firstSpouse?.spouse_email || editMemberData?.spouse_email || editMemberData?.spouseEmail || ""),
          phone: combinePhoneNumber(firstSpouse?.spouse_phone || editMemberData?.spouse_phone || editMemberData?.spousePhone, firstSpouse?.spouse_country_code || editMemberData?.spouse_country_code || editMemberData?.spouseCountryCode) || "",
          countryCode: firstSpouse?.spouse_country_code || editMemberData?.spouse_country_code || editMemberData?.spouseCountryCode || "",
          phone_type: firstSpouse?.spouse_phone_type || editMemberData?.userData?.spouse_family_members?.phone_type || editMemberData?.spouse_phone_type || editMemberData?.spousePhoneType || editMemberData?.spouseCellType || "",
          divorce: isInRelationshipOnLoad
            ? ""
            : firstSpouse?.divorce
              ? String(firstSpouse.divorce)
              : "",
          divorce_date: isInRelationshipOnLoad
            ? ""
            : formatDateForInput(firstSpouse?.divorce_date || ""),
          multiple_births_type: firstSpouse?.multiple_births_type || "",
          marriedYears: (() => {
            if (isInRelationshipOnLoad) return "";
            const weddingDate = formatDateForInput(
              firstSpouse?.wedding_anniversary ||
                editMemberData?.userData?.all_spouses?.[0]?.wedding_anniversary ||
                editMemberData?.wedding_anniversary ||
                editMemberData?.weddingAnniversary ||
                ""
            );
            return calcMarriedYears(weddingDate, {
              divorce: firstSpouse?.divorce,
              divorce_date: formatDateForInput(firstSpouse?.divorce_date || ""),
              aliveStatus: spouseAliveStatus,
              dod: formatDateForInput(
                firstSpouse?.spouse_date_of_death ||
                  editMemberData?.spouse_date_of_death ||
                  ""
              ),
              descendantAliveStatus: aliveStatus,
              descendantDod: mainMemberDOD,
            });
          })(),
        };

        setGrandfatherData(grandfatherData);

        // Create main spouse data (first spouse from all_spouses)
        const mainSpouse = memberData?.all_spouses?.[0] || {};

        const mainSpouseDOB = formatDateForInput(mainSpouse.spouse_dob || "");
        const mainSpouseDOD = formatDateForInput(mainSpouse.spouse_date_of_death || "");
        const initialMainSpouseAgeOfDeath =
          mainSpouse.spouse_present_or_deceased?.toString() === "2"
            ? calculateAge(mainSpouseDOB, mainSpouseDOD)
            : "";
        const initialMainSpouseCurrentAge =
          mainSpouse.spouse_present_or_deceased?.toString() === "1"
            ? calculateAge(mainSpouseDOB)
            : "";

        const mainSpouseData = {
          relation_history_id: mainSpouse.relation_history_id || "",
          id: 'main-spouse',
          firstName: mainSpouse.spouse_first_name || "",
          middleName: mainSpouse.spouse_middle_name || "",
          lastName: mainSpouse.spouse_last_name || "",
          suffix: mainSpouse.spouse_suffix || "",
          maidenName: mainSpouse.spouse_maiden_name || "",
          nickName: mainSpouse.spouse_nick_name || "",
          gender: mainSpouse.spouse_gender ? mainSpouse.spouse_gender.toString() : "",
          dob: mainSpouseDOB,
          stateOfBirth: mainSpouse.spouse_state_of_birth || "",
          currentStateResiding: mainSpouse.spouse_current_state_residing || mainSpouse.current_state_residing || "",
          aliveStatus: mainSpouse.spouse_present_or_deceased ? mainSpouse.spouse_present_or_deceased.toString() : "",
          dod: mainSpouseDOD,
          ageOfDeath: initialMainSpouseAgeOfDeath || mainSpouse.spouse_age_at_death || "",
          currentAge: initialMainSpouseCurrentAge,
          maritalStatus: mainSpouse.marital_status ? mainSpouse.marital_status.toString() : "",
          weddingAnniversary: isInRelationshipOnLoad
            ? ""
            : formatDateForInput(mainSpouse.wedding_anniversary || ""),
          note: mainSpouse.notes || "",
          relation: mainSpouse.spouse_relation || grandmotherData.relation,
          // Clear email if Deceased (spouse_present_or_deceased === "2")
          email: mainSpouse.spouse_present_or_deceased?.toString() === "2" ? "" : (mainSpouse.spouse_email || ""),
          ...getSpouseEmailAuxiliaryFromAddress(
            mainSpouse.spouse_present_or_deceased?.toString() === "2" ? "" : (mainSpouse.spouse_email || "")
          ),
          phone: combinePhoneNumber(mainSpouse.spouse_phone, mainSpouse.spouse_country_code) || "",
          countryCode: mainSpouse.spouse_country_code || "",
          phone_type: mainSpouse.spouse_phone_type ? mainSpouse.spouse_phone_type.toString() : "",
          divorce: isInRelationshipOnLoad
            ? ""
            : mainSpouse.divorce
              ? mainSpouse.divorce.toString()
              : "",
          divorce_date: isInRelationshipOnLoad
            ? ""
            : formatDateForInput(mainSpouse.divorce_date || ""),
          multiple_births_type: mainSpouse.multiple_births_type || "",
          wedding_anniversary: isInRelationshipOnLoad
            ? ""
            : formatDateForInput(mainSpouse.wedding_anniversary || ""),
          relation_to_head_of_line: mainSpouse.relation_to_head_of_line || grandmotherData?.relation_to_head_of_line || "",
          marriedYears: (() => {
            if (isInRelationshipOnLoad) return "";
            const weddingDate = formatDateForInput(mainSpouse.wedding_anniversary || "");
            return calcMarriedYears(weddingDate, {
              divorce: mainSpouse.divorce,
              divorce_date: formatDateForInput(mainSpouse.divorce_date || ""),
              aliveStatus: mainSpouse.spouse_present_or_deceased,
              dod: mainSpouseDOD,
              descendantAliveStatus: aliveStatus,
              descendantDod: mainMemberDOD,
            });
          })()
        };

        // Create additional spouses from remaining all_spouses
        const additionalSpousesData =
          // editMemberData.userData?.all_spouses
          memberData?.all_spouses
            ?.slice(1)
            .map((spouse, index) => {
              const spouseDOB = formatDateForInput(spouse.spouse_dob);
              const spouseDOD = formatDateForInput(spouse.spouse_date_of_death);
              const spouseAgeOfDeath =
                spouse.spouse_present_or_deceased?.toString() === "2"
                  ? calculateAge(spouseDOB, spouseDOD)
                  : "";
              const spouseCurrentAge =
                spouse.spouse_present_or_deceased?.toString() === "1"
                  ? calculateAge(spouseDOB)
                  : "";

              return {
                id: `additional-spouse-${index}`,
                relation_history_id: spouse?.relation_history_id || "",
                firstName: spouse.spouse_first_name || "",
                middleName: spouse.spouse_middle_name || "",
                specialName: spouse.spouse_special_name || "",
                lastName: spouse.spouse_last_name || "",
                suffix: spouse.spouse_suffix || "",
                maidenName: spouse.spouse_maiden_name || "",
                nickName: spouse.spouse_nick_name || "",
                gender: spouse.spouse_gender ? spouse.spouse_gender.toString() : "",
                dob: spouseDOB,
                stateOfBirth: spouse.spouse_state_of_birth || "",
                currentStateResiding: spouse.spouse_current_state_residing || spouse.current_state_residing || "",
                aliveStatus: spouse.spouse_present_or_deceased ? spouse.spouse_present_or_deceased.toString() : "",
                dod: spouseDOD,
                ageOfDeath: spouseAgeOfDeath || spouse.spouse_age_at_death || "",
                currentAge: spouseCurrentAge,
                note: spouse.notes || ``,
                relation: spouse.spouse_relation || grandmotherData?.relation,
                // Clear email if Deceased (spouse_present_or_deceased === "2")
                email: spouse.spouse_present_or_deceased?.toString() === "2" ? "" : (spouse.spouse_email || ""),
                ...getSpouseEmailAuxiliaryFromAddress(
                  spouse.spouse_present_or_deceased?.toString() === "2" ? "" : (spouse.spouse_email || "")
                ),
                phone: combinePhoneNumber(spouse.spouse_phone, spouse.spouse_country_code) || "",
                countryCode: spouse.spouse_country_code || "",
                phone_type: spouse.spouse_phone_type ? spouse.spouse_phone_type.toString() : "",
                divorce: isInRelationshipOnLoad
                  ? ""
                  : spouse.divorce
                    ? spouse.divorce.toString()
                    : "",
                divorce_date: isInRelationshipOnLoad
                  ? ""
                  : formatDateForInput(spouse.divorce_date),
                multiple_births_type: spouse.multiple_births_type || "",
                relation_to_head_of_line: spouse.relation_to_head_of_line || "",
                wedding_anniversary: isInRelationshipOnLoad
                  ? ""
                  : formatDateForInput(spouse.wedding_anniversary),
                marriedYears: (() => {
                  if (isInRelationshipOnLoad) return "";
                  const weddingDate = formatDateForInput(spouse.wedding_anniversary || "");
                  return calcMarriedYears(weddingDate, {
                    divorce: spouse.divorce,
                    divorce_date: formatDateForInput(spouse.divorce_date || ""),
                    aliveStatus: spouse.spouse_present_or_deceased,
                    dod: spouseDOD,
                    descendantAliveStatus: aliveStatus,
                    descendantDod: mainMemberDOD,
                  });
                })()
              };
            }) || [];

        // Set all spouses
        const allSpouses = [mainSpouseData, ...additionalSpousesData];
        setSpouses(allSpouses);
        console.log("all spouses : ",allSpouses);

        // Open sections for all spouses
        setOpenSpouseSections(allSpouses.map((spouse) => spouse.id));

        // Handle images
        const existingImages = [];

        // Main member image
        if (editMemberData.userData?.member_images && Array.isArray(editMemberData.userData.member_images)) {
          editMemberData.userData.member_images.forEach((img, index) => {
            if (img.role === 0) {
              existingImages.push({
                id: `existing-main-image-${index}`,
                unique_image_id: img.id,
                url: img.image,
                file: null,
                type: "main",
              });
            }
          });
        }      // Spouse images from all_spouses array
        if (editMemberData.userData?.all_spouses && Array.isArray(editMemberData.userData.all_spouses)) {
          editMemberData.userData.all_spouses.forEach((spouse, spouseIndex) => {
            const spouseId = allSpouses[spouseIndex]?.id || `spouse-${spouseIndex}`;          // Get spouse images from spouse_images array
            if (spouse.spouse_images && Array.isArray(spouse.spouse_images)) {
              spouse.spouse_images.forEach((img, imgIndex) => {
                if (img.role === 1) {
                  existingImages.push({
                    id: `existing-spouse-${spouseIndex}-${imgIndex}`,
                    unique_image_id: img.id,
                    url: img.image,
                    file: null,
                    type: spouseIndex === 0 ? `spouse-main-spouse` : `spouse-${spouseId}`,
                  });
                }
              });
            }
          });
        }
        setUploadedImages(existingImages);

      } else {
        // Reset form for new member creation
        // ... (Existing reset logic)
        // Only reset isAdmin when opening modal for new member
        // Don't reset during close animation to prevent visual glitch
        if (!isEditMode) {
          setIsAdmin(false);
          setPrimaryLabelName("");
          setSpouseLabelName("");
          setPrimaryRelationName("");
          setSpouseRelationName("");
        }
        setGrandfatherData({
          firstName: "",
          middleName: "",
          specialName: "",
          lastName: "",
          suffix: "",
          maidenName: "",
          nickName: "",
          // gender: "",
          gender:
            rootHeadData?.selectedHeadName == "Son" || rootHeadData?.selectedHeadName == "Grandfather"
              ? 1
              : rootHeadData?.selectedHeadName == "Daughter" || rootHeadData?.selectedHeadName == "GrandMother"
                ? 2
                : "",
          dob: "",
          stateOfBirth: "",
          currentStateResiding: "",
          aliveStatus: "",
          dod: "",
          ageOfDeath: "",
          currentAge: "", // Ensure currentAge is reset
          maritalStatus: "",
          weddingAnniversary: "",
          note: "",
          relation: primaryRel,
          email: "",
          phone: "",
          countryCode: "",
          phone_type: "",
          relation_to_head_of_line: "",
          bio_descendant_parent: "",
          bio_non_descendant_parent: "",
          step_parent: "",
          adopted_parent: "",
          multiple_births_type: "",
          multiple_birth_with_member: "",
          multiple_birth_order: "",
        });

        setSpouses([
          {
            id: "main-spouse",
            firstName: "",
            middleName: "",
            specialName: "",
            lastName: "",
            suffix: "",
            maidenName: "",
            nickName: "",
            gender:
              rootHeadData?.selectedHeadName == "Son" || rootHeadData?.selectedHeadName == "Grandfather"
                ? 2
                : rootHeadData?.selectedHeadName == "Daughter" || rootHeadData?.selectedHeadName == "GrandMother"
                  ? 1
                  : "",
            dob: "",
            stateOfBirth: "",
            currentStateResiding: "",
            aliveStatus: "",
            dod: "",
            ageOfDeath: "",
            currentAge: "", // Ensure currentAge is reset
            maritalStatus: "",
            weddingAnniversary: "",
            note: "",
            relation: spouseRel,
            email: "",
            email_domain: "",
            email_custom_domain: "",
            phone: "",
            countryCode: "",
            phone_type: "",
          },
        ]);
        setOpenSpouseSections(["main-spouse"]);
        // Only reset uploaded images for new member creation, not for edit mode
        if (!isEditMode) {
          setUploadedImages([]);
          setPrimaryLabelName("");
          setSpouseLabelName("");
          setPrimaryRelationName("");
          setSpouseRelationName("");
        }
      }

      setOpenSection("primary");

      // Reset form submission state and touched fields when modal opens
      setFormSubmitted(false);
      setTouchedFields({
        primary: { dob: false, dod: false, weddingAnniversary: false, maritalStatus: false, email: false, aliveStatus: false },
        spouse: { dob: false, dod: false, weddingAnniversary: false, divorce: false, email: false, aliveStatus: false, firstName: false, lastName: false },
      });
      setDateValidationErrors({
        primary: { dob: "", dod: "", weddingAnniversary: "" },
        spouse: { dob: "", dod: "", weddingAnniversary: "" },
      });
      setFieldValidationErrors({
        maritalStatus: "",
        email: "",
        aliveStatus: "",
      });
      setSpouseValidationErrors({
        divorce: "",
        email: "",
        aliveStatus: "",
        firstName: "",
        lastName: "",
        image: "",
        gender: ""
      });
      setAdditionalSpouseImageErrors({});
    }
  }, [
    isOpen,
    isRootGeneration,
    parentGeneration,
    rootHeadGeneration,
    effectiveGeneration,
    isEditMode,
    editMemberData,
    rootHeadGenerationName,
    fetchedMemberData
  ]);

  // Reset isAdmin after modal closes (after animation completes to prevent visual glitch)
  useEffect(() => {
    if (!isOpen) {
      // Reset isAdmin after close animation completes (200ms to match closeTimeoutMS)
      const timeoutId = setTimeout(() => {
        setIsAdmin(false);
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  // 3. EFFECT HOOK FOR GRANDFATHER AGE CALCULATION
  useEffect(() => {
    const { aliveStatus, dob, dod } = grandfatherData;
    let newAgeOfDeath = "";
    let newCurrentAge = "";

    // If Deceased (status: 2) AND DOB and DOD are available
    if (aliveStatus == "2" && dob && dod) {
      newAgeOfDeath = calculateAge(dob, dod);
    }
    // If Living (status: 1) AND DOB is available
    else if (aliveStatus == "1" && dob) {
      newCurrentAge = calculateAge(dob);
    }
    // If status is not set, but DOD is entered
    else if (!aliveStatus && dob && dod) {
      newAgeOfDeath = calculateAge(dob, dod);
    }
    // If status is not set, but DOD is NOT entered
    else if (!aliveStatus && dob) {
      newCurrentAge = calculateAge(dob);
    }

    setGrandfatherData((prev) => ({
      ...prev,
      ageOfDeath: newAgeOfDeath,
      currentAge: newCurrentAge,
      // Clear DOD if living is selected
      dod: aliveStatus == "1" ? "" : prev.dod,
    }));
  }, [grandfatherData.aliveStatus, grandfatherData.dob, grandfatherData.dod]);

  // Effect to re-validate dates when they change (for cross-field validation)
  useEffect(() => {
    // Only validate if dates are touched or form is submitted
    if (
      touchedFields.primary.dob ||
      touchedFields.primary.dod ||
      touchedFields.primary.weddingAnniversary ||
      formSubmitted
    ) {
      const primaryDateErrors = validatePrimaryMemberDates({
        dob: grandfatherData.dob,
        dod: grandfatherData.dod,
        weddingAnniversary: grandfatherData.weddingAnniversary,
      });

      // Validate child birth date is not earlier than parent birth date (if parent data is available)
      if (
        parentMember &&
        parentMember.dob &&
        isDateComplete(parentMember.dob) &&
        grandfatherData.dob &&
        isDateComplete(grandfatherData.dob)
      ) {
        const childBirthError = validateChildBirthDate(
          grandfatherData.dob,
          parentMember.dob
        );
        if (childBirthError) {
          primaryDateErrors.dob = childBirthError;
        }
      }

      // Validate DOB matches selected sibling's DOB for multiple births
      if (
        grandfatherData.multiple_births_type &&
        grandfatherData.multiple_birth_with_member &&
        parentChildrenData &&
        parentChildrenData.length > 0 &&
        grandfatherData.dob &&
        isDateComplete(grandfatherData.dob)
      ) {
        const selectedSibling = parentChildrenData.find(
          child => child.id?.toString() === grandfatherData.multiple_birth_with_member?.toString()
        );
        
        if (selectedSibling && selectedSibling.dob) {
          // Convert sibling DOB to MM/DD/YYYY format for comparison
          let siblingDOBFormatted = "";
          if (typeof selectedSibling.dob === 'string' && selectedSibling.dob.includes('/')) {
            // Already in MM/DD/YYYY format
            siblingDOBFormatted = selectedSibling.dob;
          } else if (selectedSibling.dob) {
            // Convert timestamp to MM/DD/YYYY
            const timestamp = typeof selectedSibling.dob === 'string' ? parseInt(selectedSibling.dob) : selectedSibling.dob;
            if (!isNaN(timestamp) && timestamp !== 0) {
              const date = new Date(timestamp);
              if (!isNaN(date.getTime())) {
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(date.getUTCDate()).padStart(2, '0');
                const year = date.getUTCFullYear();
                siblingDOBFormatted = `${month}/${day}/${year}`;
              }
            }
          }

          // Compare DOBs (normalize both to same format for comparison)
          // Convert entered DOB to MM/DD/YYYY format for comparison
          let enteredDOBFormatted = "";
          if (grandfatherData.dob) {
            // CustomDatePicker uses YYYY/MM/DD format, convert to MM/DD/YYYY for comparison
            if (grandfatherData.dob.includes('/')) {
              const parts = grandfatherData.dob.split('/');
              if (parts.length === 3) {
                // Assume YYYY/MM/DD format from CustomDatePicker
                enteredDOBFormatted = `${parts[1]}/${parts[2]}/${parts[0]}`;
              } else {
                enteredDOBFormatted = grandfatherData.dob.trim();
              }
            } else {
              enteredDOBFormatted = grandfatherData.dob.trim();
            }
          }
          
          // Only show error if DOBs don't match AND the field was manually touched/changed
          // Don't show error immediately after auto-fill - only if user manually changes it
          if (siblingDOBFormatted && enteredDOBFormatted) {
            if (enteredDOBFormatted !== siblingDOBFormatted) {
              // DOBs don't match - only show error if user manually changed the DOB
              // Check if DOB field was manually changed (touched by user after auto-fill)
              if (touchedFields.primary.dob) {
                const siblingName = selectedSibling.first_name || selectedSibling.name?.split(' ')[0] || "selected sibling";
                primaryDateErrors.dob = `Date of birth must match ${siblingName}'s date of birth (${siblingDOBFormatted}) for multiple births.`;
              }
              // If not touched, don't show error (it was just auto-filled)
            } else {
              // DOBs match - clear any previous multiple birth DOB error
              if (primaryDateErrors.dob && primaryDateErrors.dob.includes('must match')) {
                // Only clear if it was a multiple birth error, keep other errors
                const otherErrors = validatePrimaryMemberDates({
                  dob: grandfatherData.dob,
                  dod: grandfatherData.dod,
                  weddingAnniversary: grandfatherData.weddingAnniversary,
                });
                if (otherErrors.dob && !otherErrors.dob.includes('must match')) {
                  primaryDateErrors.dob = otherErrors.dob;
                } else if (!otherErrors.dob) {
                  delete primaryDateErrors.dob;
                }
              }
            }
          }
        }
      }

      // Cross-validate with all spouse dates (if spouse exists and is not single)
      const isSingle = grandfatherData.maritalStatus === "1";
      if (!isSingle && spouses.length > 0) {
        // Cross-validate with main spouse (index 0)
        if (spouses[0]) {
          const mainSpouse = spouses[0];
          const crossErrors = validateCrossSpouseDates(
            {
              dob: grandfatherData.dob,
              dod: grandfatherData.dod,
              weddingAnniversary: grandfatherData.weddingAnniversary,
            },
            {
              dob: mainSpouse.dob,
              dod: mainSpouse.dod,
              weddingAnniversary: mainSpouse.weddingAnniversary,
              divorce_date: mainSpouse.divorce_date,
            }
          );

          // Merge cross-validation errors with existing errors
          if (crossErrors.primary.dob && !primaryDateErrors.dob) {
            primaryDateErrors.dob = crossErrors.primary.dob;
          }
          if (crossErrors.primary.dod && !primaryDateErrors.dod) {
            primaryDateErrors.dod = crossErrors.primary.dod;
          }
          if (crossErrors.primary.weddingAnniversary && !primaryDateErrors.weddingAnniversary) {
            primaryDateErrors.weddingAnniversary = crossErrors.primary.weddingAnniversary;
          }

          // Also update spouse errors from cross-validation
          setDateValidationErrors((prev) => ({
            ...prev,
            spouse: {
              ...prev.spouse,
              ...crossErrors.spouse,
            },
          }));
        }

        // Cross-validate with all additional spouses (index 1 and beyond)
        spouses.slice(1).forEach((spouse, index) => {
          const crossErrors = validateCrossSpouseDates(
            {
              dob: grandfatherData.dob,
              dod: grandfatherData.dod,
              weddingAnniversary: grandfatherData.weddingAnniversary,
            },
            {
              dob: spouse.dob,
              dod: spouse.dod,
              weddingAnniversary:
                spouse.wedding_anniversary || spouse.weddingAnniversary,
              divorce_date: spouse.divorce_date,
            }
          );

          // Merge cross-validation errors with existing primary errors (take the first error found)
          if (crossErrors.primary.dob && !primaryDateErrors.dob) {
            primaryDateErrors.dob = crossErrors.primary.dob;
          }
          if (crossErrors.primary.dod && !primaryDateErrors.dod) {
            primaryDateErrors.dod = crossErrors.primary.dod;
          }
          if (crossErrors.primary.weddingAnniversary && !primaryDateErrors.weddingAnniversary) {
            primaryDateErrors.weddingAnniversary = crossErrors.primary.weddingAnniversary;
          }

          // Update additional spouse errors from cross-validation
          setDateValidationErrors((prev) => {
            const currentAdditionalSpouseErrors = prev.additional_spouse || [];
            const updatedErrors = [...currentAdditionalSpouseErrors];

            // Ensure the array is long enough
            while (updatedErrors.length <= index) {
              updatedErrors.push({
                dob: "",
                dod: "",
                wedding_anniversary: "",
                divorce_date: "",
              });
            }

            // Merge cross-validation errors with existing errors
            updatedErrors[index] = {
              ...updatedErrors[index],
              ...(crossErrors.spouse.dob && !updatedErrors[index].dob
                ? { dob: crossErrors.spouse.dob }
                : {}),
              ...(crossErrors.spouse.dod && !updatedErrors[index].dod
                ? { dod: crossErrors.spouse.dod }
                : {}),
              ...(crossErrors.spouse.weddingAnniversary && !updatedErrors[index].wedding_anniversary
                ? { wedding_anniversary: crossErrors.spouse.weddingAnniversary }
                : {}),
              ...(crossErrors.spouse.divorce_date && !updatedErrors[index].divorce_date
                ? { divorce_date: crossErrors.spouse.divorce_date }
                : {}),
            };

            return {
              ...prev,
              additional_spouse: updatedErrors,
            };
          });
        });
      }

      setDateValidationErrors((prev) => ({
        ...prev,
        primary: {
          ...prev.primary,
          ...primaryDateErrors,
        },
      }));
    }
  }, [
    grandfatherData.dob,
    grandfatherData.dod,
    grandfatherData.weddingAnniversary,
    grandfatherData.maritalStatus,
    grandfatherData.multiple_birth_with_member,
    grandfatherData.multiple_births_type,
    touchedFields.primary.dob,
    touchedFields.primary.dod,
    touchedFields.primary.weddingAnniversary,
    formSubmitted,
    parentMember?.dob,
    parentChildrenData,
    spouses,
  ]);

  // Effect to re-validate additional spouse dates when they change (for cross-field validation)
  useEffect(() => {
    // Validate each additional spouse (spouses.slice(1))
    spouses.slice(1).forEach((spouse, index) => {
      const spouseTouchedFields =
        touchedFields.additional_spouse?.[index] || {};

      // Only validate if dates are touched or form is submitted
      if (
        spouseTouchedFields.dob ||
        spouseTouchedFields.dod ||
        spouseTouchedFields.wedding_anniversary ||
        spouseTouchedFields.divorce_date ||
        formSubmitted
      ) {
        const additionalSpouseDateErrors = validateAdditionalSpouseDates({
          dob: spouse.dob,
          dod: spouse.dod,
          weddingAnniversary:
            spouse.wedding_anniversary || spouse.weddingAnniversary,
          divorce_date: spouse.divorce_date,
        });

        // Cross-validate with primary member dates
        const isSingle = grandfatherData.maritalStatus === "1";
        if (!isSingle) {
          const crossErrors = validateCrossSpouseDates(
            {
              dob: grandfatherData.dob,
              dod: grandfatherData.dod,
              weddingAnniversary: grandfatherData.weddingAnniversary,
            },
            {
              dob: spouse.dob,
              dod: spouse.dod,
              weddingAnniversary:
                spouse.wedding_anniversary || spouse.weddingAnniversary,
              divorce_date: spouse.divorce_date,
            }
          );

          // Merge cross-validation errors with existing additional spouse errors
          if (crossErrors.spouse.dob && !additionalSpouseDateErrors.dob) {
            additionalSpouseDateErrors.dob = crossErrors.spouse.dob;
          }
          if (crossErrors.spouse.dod && !additionalSpouseDateErrors.dod) {
            additionalSpouseDateErrors.dod = crossErrors.spouse.dod;
          }
          if (crossErrors.spouse.weddingAnniversary && !additionalSpouseDateErrors.weddingAnniversary) {
            additionalSpouseDateErrors.weddingAnniversary = crossErrors.spouse.weddingAnniversary;
          }
          if (crossErrors.spouse.divorce_date && !additionalSpouseDateErrors.divorce_date) {
            additionalSpouseDateErrors.divorce_date = crossErrors.spouse.divorce_date;
          }

          // Also update primary member errors from cross-validation
          setDateValidationErrors((prev) => ({
            ...prev,
            primary: {
              ...prev.primary,
              ...crossErrors.primary,
            },
          }));
        }

        setDateValidationErrors((prev) => {
          const currentAdditionalSpouseErrors = prev.additional_spouse || [];
          const updatedErrors = [...currentAdditionalSpouseErrors];

          // Ensure the array is long enough
          while (updatedErrors.length <= index) {
            updatedErrors.push({
              dob: "",
              dod: "",
              wedding_anniversary: "",
              divorce_date: "",
            });
          }

          updatedErrors[index] = {
            ...updatedErrors[index],
            ...additionalSpouseDateErrors,
            // Map weddingAnniversary to wedding_anniversary for consistency
            wedding_anniversary:
              additionalSpouseDateErrors.weddingAnniversary || "",
          };

          return {
            ...prev,
            additional_spouse: updatedErrors,
          };
        });
      }
    });
  }, [
    spouses
      .slice(1)
      .map(
        (s) =>
          `${s.dob}-${s.dod}-${s.wedding_anniversary || s.weddingAnniversary}-${s.divorce_date}`
      )
      .join("|"),
    touchedFields.additional_spouse,
    formSubmitted,
    grandfatherData.dob,
    grandfatherData.dod,
    grandfatherData.weddingAnniversary,
    grandfatherData.maritalStatus,
  ]);

  // Effect to re-validate main spouse dates when they change (for cross-field validation)
  useEffect(() => {
    const mainSpouse = spouses[0];
    if (!mainSpouse) return;

    // Only validate if dates are touched or form is submitted
    if (
      touchedFields.spouse.dob ||
      touchedFields.spouse.dod ||
      touchedFields.spouse.weddingAnniversary ||
      touchedFields.spouse.divorce_date ||
      formSubmitted
    ) {
      const spouseDateErrors = validateSpouseDates({
        dob: mainSpouse.dob,
        dod: mainSpouse.dod,
        weddingAnniversary: mainSpouse.weddingAnniversary,
        divorce_date: mainSpouse.divorce_date,
      });

      // Cross-validate with primary member dates
      const isSingle = grandfatherData.maritalStatus === "1";
      if (!isSingle) {
        const crossErrors = validateCrossSpouseDates(
          {
            dob: grandfatherData.dob,
            dod: grandfatherData.dod,
            weddingAnniversary: grandfatherData.weddingAnniversary,
          },
          {
            dob: mainSpouse.dob,
            dod: mainSpouse.dod,
            weddingAnniversary: mainSpouse.weddingAnniversary,
            divorce_date: mainSpouse.divorce_date,
          }
        );

        // Merge cross-validation errors with existing spouse errors
        if (crossErrors.spouse.dob && !spouseDateErrors.dob) {
          spouseDateErrors.dob = crossErrors.spouse.dob;
        }
        if (crossErrors.spouse.dod && !spouseDateErrors.dod) {
          spouseDateErrors.dod = crossErrors.spouse.dod;
        }
        if (crossErrors.spouse.weddingAnniversary && !spouseDateErrors.weddingAnniversary) {
          spouseDateErrors.weddingAnniversary = crossErrors.spouse.weddingAnniversary;
        }
        if (crossErrors.spouse.divorce_date && !spouseDateErrors.divorce_date) {
          spouseDateErrors.divorce_date = crossErrors.spouse.divorce_date;
        }

        // Also update primary member errors from cross-validation
        setDateValidationErrors((prev) => ({
          ...prev,
          primary: {
            ...prev.primary,
            ...crossErrors.primary,
          },
        }));
      }

      setDateValidationErrors((prev) => ({
        ...prev,
        spouse: {
          ...prev.spouse,
          ...spouseDateErrors,
        },
      }));
    }
  }, [
    spouses[0]?.dob,
    spouses[0]?.dod,
    spouses[0]?.weddingAnniversary,
    spouses[0]?.divorce_date,
    grandfatherData.dob,
    grandfatherData.dod,
    grandfatherData.weddingAnniversary,
    grandfatherData.maritalStatus,
    touchedFields.spouse.dob,
    touchedFields.spouse.dod,
    touchedFields.spouse.weddingAnniversary,
    touchedFields.spouse.divorce_date,
    formSubmitted,
  ]);

  useEffect(() => {
    // console.log("rootHeadGenerationName in relation useEffect : ", rootHeadGenerationName);
    if (rootHeadGenerationName) {
      // Clear old relation data to prevent conflicts when recalculating
      // Clear all relation-related state first to prevent old data conflicts
      setPrimaryLabelName("");
      setSpouseLabelName("");
      setPrimaryRelationName("");
      setSpouseRelationName("");
      setGrandfatherData((prev) => ({
        ...prev,
        relation: "",
        relation_to_head_of_line: "",
      }));
      setSpouses((prev) => {
        const newSpouses = [...prev];
        if (newSpouses[0]) {
          newSpouses[0] = {
            ...newSpouses[0],
            relation: "",
            relation_to_head_of_line: "",
          };
        }
        return newSpouses;
      });

      const { primaryLabel, spouseLabel, primaryRelation, spouseRelation } =
        getFamilyLabels(
          isRootGeneration,
          effectiveGeneration,
          rootHeadGenerationName
        );
      setPrimaryLabelName(primaryLabel);
      setSpouseLabelName(spouseLabel);
      setPrimaryRelationName(primaryRelation);
      setSpouseRelationName(spouseRelation);


      // Get Top of Line relations
      const familyLabels = getFamilyLabels(
        isRootGeneration,
        effectiveGeneration,
        rootHeadGenerationName
      );
      // SIMPLE Head of Line calculation based on Top of Line response
      const headOfLineRelations = calculateHeadOfLineFromFamilyLabels(
        familyLabels,
        isRootGeneration
      );

      // Convert relations based on current gender
      // Gender: 1 = Male, 2 = Female
      const currentGender = grandfatherData.gender;

      // But if relation already exists in state (from previous render), use that for conversion
      //todo remove any conditions to use previous render state if any is present to make sure we use only fresh data
      const currentPrimaryRelation = primaryRelation ||grandfatherData.relation ;
      const currentSpouseRelation = spouseRelation || spouses[0]?.relation;

      let finalPrimaryRelation = currentPrimaryRelation;
      let finalSpouseRelation = currentSpouseRelation;

      let tempSpouseRelation = currentSpouseRelation;

      if (currentGender) {
        // Convert primary relation to match current gender
        // finalPrimaryRelation = convertRelationByGender(primaryRelation, currentGender);
        finalPrimaryRelation = convertRelationByGender(currentPrimaryRelation, currentGender);
        // Convert spouse relation to opposite of primary
        tempSpouseRelation = convertRelationByGender(
          // spouseRelation,
          currentSpouseRelation,
          currentGender == "1" ? "2" : "1"
        );
      }

      // For head of line relations, we need to recalculate based on converted relations
      // Recalculate head of line with converted relations
      const convertedFamilyLabels = {
        primaryRelation: finalPrimaryRelation,
        // spouseRelation: finalSpouseRelation
        spouseRelation: tempSpouseRelation
      };
      const convertedHeadOfLineRelations = calculateHeadOfLineFromFamilyLabels(
        convertedFamilyLabels,
        isRootGeneration
      );

      let finalPrimaryHeadOfLine = convertedHeadOfLineRelations.primaryHeadOfLineRelation || headOfLineRelations.primaryHeadOfLineRelation;
      let finalSpouseHeadOfLine = convertedHeadOfLineRelations.spouseHeadOfLineRelation || headOfLineRelations.spouseHeadOfLineRelation;

      // Set the relations
      setGrandfatherData((prev) => ({
        ...prev,
        // relation: primaryRelation,
        relation: finalPrimaryRelation,
        // relation_to_head_of_line: headOfLineRelations.primaryHeadOfLineRelation,
        relation_to_head_of_line: finalPrimaryHeadOfLine,
      }));
      setPrimaryRelationName(finalPrimaryRelation);

      console.log("final primary relation : ",finalPrimaryRelation)


      setSpouses((prev) => {
        const newSpouses = [...prev];
        newSpouses[0] = {
          ...newSpouses[0],
          // relation: spouseRelation,
          relation: finalSpouseRelation,
          relation_to_head_of_line:
            // headOfLineRelations.spouseHeadOfLineRelation,
            finalSpouseHeadOfLine,
        };
        return newSpouses;
      });


    } else if (isEditMode && grandfatherData?.gender && grandfatherData?.relation) {
      // Handle edit mode when rootHeadGenerationName is not available
      const currentGender = grandfatherData.gender;
      const currentPrimaryRelation = grandfatherData.relation;
      const currentSpouseRelation = spouses[0]?.relation || "";
      const currentPrimaryHeadOfLine = grandfatherData?.relation_to_head_of_line || "";
      const currentSpouseHeadOfLine = spouses[0]?.relation_to_head_of_line || "";

      // Convert relations based on current gender
      const finalPrimaryRelation = convertRelationByGender(currentPrimaryRelation, currentGender);
      const finalSpouseRelation = currentSpouseRelation
        ? convertRelationByGender(currentSpouseRelation, currentGender == "1" ? "2" : "1")
        : "";

      // Convert relation_to_head_of_line based on current gender
      const finalPrimaryHeadOfLine = currentPrimaryHeadOfLine
        ? convertRelationByGender(currentPrimaryHeadOfLine, currentGender)
        : "";
      const finalSpouseHeadOfLine = currentSpouseHeadOfLine
        ? convertRelationByGender(currentSpouseHeadOfLine, currentGender == "1" ? "2" : "1")
        : "";

      const convertedFamilyLabels = {
        primaryRelation: finalPrimaryRelation,
        spouseRelation: finalSpouseRelation
      };
      const convertedHeadOfLineRelations = calculateHeadOfLineFromFamilyLabels(
        convertedFamilyLabels,
        false
      );


      // Update primary member relation
      setGrandfatherData((prev) => ({
        ...prev,
        relation: finalPrimaryRelation,
        // relation_to_head_of_line: finalPrimaryHeadOfLine,
        relation_to_head_of_line: convertedHeadOfLineRelations?.primaryHeadOfLineRelation
      }));

      // Update spouse relation
      // if (finalSpouseRelation || finalSpouseHeadOfLine) {
      //   setSpouses((prev) => {
      //     const newSpouses = [...prev];
      //     if (newSpouses[0]) {
      //       newSpouses[0] = {
      //         ...newSpouses[0],
      //         ...(finalSpouseRelation && { relation: finalSpouseRelation }),
      //         ...(finalSpouseHeadOfLine && { relation_to_head_of_line: finalSpouseHeadOfLine }),
      //       };
      //     }
      //     return newSpouses;
      //   });
      // }
    }
  }, [isRootGeneration, effectiveGeneration, rootHeadGenerationName, grandfatherData?.gender,parentMember]);

  // Separate useEffect to handle spouse gender changes - only convert spouse relations
  useEffect(() => {
    spouses.forEach((spouse, index) => {
      if (spouse?.gender && spouse?.relation) {
        const finalSpouseRelation = convertRelationByGender(spouse.relation, spouse.gender);
        const currentSpouseHeadOfLine = spouse.relation_to_head_of_line || "";
        const finalSpouseHeadOfLine = currentSpouseHeadOfLine
          ? convertRelationByGender(currentSpouseHeadOfLine, spouse.gender)
          : "";

        console.log("final spouse relations : ", finalSpouseRelation)

        // Only update if relation actually changed
        if (finalSpouseRelation !== spouse.relation || finalSpouseHeadOfLine !== currentSpouseHeadOfLine) {
          setSpouses((prev) => {
            const newSpouses = [...prev];
            if (newSpouses[index]) {
              newSpouses[index] = {
                ...newSpouses[index],
                relation: finalSpouseRelation,
                relation_to_head_of_line: finalSpouseHeadOfLine,
              };
            }
            return newSpouses;
          });
        }
      }
    });
  }, [spouses.map(s => s?.gender).join(","), grandmotherData?.gender]);

  useEffect(() => {
    if (
      primaryLabelName &&
      spouseLabelName &&
      primaryRelationName &&
      spouseRelationName
    ) {
      primaryLabel = primaryLabelName;
      spouseLabel = spouseLabelName;
      primaryRelation = primaryRelationName;
      spouseRelation = spouseRelationName;
    }
  }, [
    primaryLabelName,
    spouseLabelName,
    primaryRelationName,
    spouseRelationName,
    grandfatherData?.gender
  ]);

  // useEffect to handle age calculations and married years for all spouses
  useEffect(() => {
    const updatedSpouses = spouses.map((spouse) => {
      const { aliveStatus, dob, dod, divorce, divorce_date } = spouse;
      let newAgeOfDeath = "";
      let newCurrentAge = "";
      let marriedYears = "";

      // Calculate age
      if (aliveStatus == "2" && dob && dod) {
        newAgeOfDeath = calculateAge(dob, dod);
      } else if (aliveStatus == "1" && dob) {
        newCurrentAge = calculateAge(dob);
      } else if (!aliveStatus && dob && dod) {
        newAgeOfDeath = calculateAge(dob, dod);
      } else if (!aliveStatus && dob) {
        newCurrentAge = calculateAge(dob);
      }

      const weddingDate = spouse.wedding_anniversary || spouse.weddingAnniversary;
      marriedYears = calcMarriedYears(weddingDate, {
        divorce,
        divorce_date,
        aliveStatus,
        dod,
        descendantAliveStatus: grandfatherData.aliveStatus,
        descendantDod: grandfatherData.dod,
      });

      return {
        ...spouse,
        ageOfDeath: newAgeOfDeath,
        currentAge: newCurrentAge,
        marriedYears: marriedYears,
        dod: aliveStatus == "1" ? "" : spouse.dod,
      };
    });

    // Only update if there are actual changes to prevent unnecessary re-renders
    if (JSON.stringify(updatedSpouses) !== JSON.stringify(spouses)) {
      setSpouses(updatedSpouses);
    }
  }, [
    spouses
      .map((spouse) => `${spouse.dob}-${spouse.dod}-${spouse.aliveStatus}-${spouse.wedding_anniversary || spouse.weddingAnniversary}-${spouse.divorce}-${spouse.divorce_date}`)
      .join("-"),
    grandfatherData.aliveStatus,
    grandfatherData.dod,
  ]);

  const handleGrandfatherChange = (e) => {
    const { name, value } = e.target;

    // In handleGrandfatherChange, add this condition:
    if (name === "maritalStatus" && value !== "6") {
      // Clear additional spouses if not Re-Married, keep only main spouse
      // setSpouses((prev) => [prev[0]]);
      setSpouses((prev) => {
        const newSpouses = [prev[0] || { id: "main-spouse", gender: "" }];
        // Initialize spouse gender when changing from Single to Married
        if ((value == 2) || (value == 6)) {
          const primaryGender = grandfatherData.gender;
          newSpouses[0].gender = primaryGender == 1 ? 2 : 1;

          // if(grandfatherData?.relation){
          //   newSpouses[0].relation = convertRelationByGender(grandfatherData?.relation, newSpouses[0].gender == 1 ? 2 : 1);

          // }
          // : 
          // (rootHeadData?.selectedHeadName == "Son" || rootHeadData?.selectedHeadName == "Grandfather" ? "2" : 
          //  rootHeadData?.selectedHeadName == "Daughter" || rootHeadData?.selectedHeadName == "GrandMother" ? "1" : "");
        }
        console.log("new spouses : ", newSpouses, " primary gender : ", grandfatherData.gender);
        return newSpouses;
      });
      setOpenSpouseSections(["main-spouse"]);
    }
    else // Handle Re-Married - initialize main spouse gender without clearing additional spouses
      if (name === "maritalStatus" && value === "6") {
        setSpouses((prev) => {
          const newSpouses = [...prev];
          // Ensure main spouse exists
          if (!newSpouses[0]) {
            newSpouses[0] = { id: "main-spouse", gender: "" };
          }
          // Initialize main spouse gender if not set
          // if (!newSpouses[0].gender) {
          const primaryGender = grandfatherData.gender;
          const spouseGender = primaryGender ?
            (String(primaryGender) == "1" ? "2" : "1") :
            (rootHeadData?.selectedHeadName == "Son" || rootHeadData?.selectedHeadName == "Grandfather" ? "2" :
              rootHeadData?.selectedHeadName == "Daughter" || rootHeadData?.selectedHeadName == "GrandMother" ? "1" : "");
          newSpouses[0].gender = spouseGender;
          // }


          // if(grandfatherData?.relation){
          //   newSpouses[0].relation = convertRelationByGender(grandfatherData?.relation, newSpouses[0].gender == 1 ? 2 : 1);

          // }

          return newSpouses;
        });
      }

    // Handle text input fields - trim leading/trailing spaces
    const textFields = [
      "firstName",
      "middleName",
      "specialName",
      "lastName",
      "suffix",
      "maidenName",
      "nickName",
      "note",
      "email",
      "bio_descendant_parent",
      "bio_non_descendant_parent",
      "step_parent",
      "adopted_parent",
    ];

    if (textFields.includes(name)) {
      // Trim the value on change (remove leading/trailing spaces)
      const trimmedValue = value.trimStart(); // Allow spaces in the middle, but trim leading spaces
      setGrandfatherData((prev) => ({ ...prev, [name]: trimmedValue }));
      return;
    }

    // Handle date fields with validation
    if (name === "dob" || name === "dod" || name === "weddingAnniversary") {
      setTouchedFields((prev) => ({
        ...prev,
        primary: {
          ...prev.primary,
          [name]: true,
          // Also mark related fields as touched for cross-validation
          ...(name === "dob" ? { dod: true, weddingAnniversary: true } : {}),
          ...(name === "dod" ? { dob: true } : {}),
          ...(name === "weddingAnniversary" ? { dob: true } : {}),
        },
      }));

      // Update state and validate dates with updated value
      setGrandfatherData((prev) => {
        const updatedData = { ...prev, [name]: value };

        // Validate all date relationships
        const primaryDateErrors = validatePrimaryMemberDates({
          dob: updatedData.dob,
          dod: updatedData.dod,
          weddingAnniversary: updatedData.weddingAnniversary,
        });

        // Validate child birth date is not earlier than parent birth date (if parent data is available)
        if (
          name === "dob" &&
          parentMember &&
          parentMember.dob &&
          isDateComplete(parentMember.dob) &&
          value &&
          isDateComplete(value)
        ) {
          const childBirthError = validateChildBirthDate(
            value,
            parentMember.dob
          );
          if (childBirthError) {
            primaryDateErrors.dob = childBirthError;
          }
        }

        // Cross-validate with all spouse dates (if spouse exists and is not single)
        const isSingle = updatedData.maritalStatus === "1";
        if (!isSingle && spouses.length > 0) {
          // Cross-validate with main spouse (index 0)
          if (spouses[0]) {
            const mainSpouse = spouses[0];
            const crossErrors = validateCrossSpouseDates(updatedData, {
              dob: mainSpouse.dob,
              dod: mainSpouse.dod,
              weddingAnniversary: mainSpouse.weddingAnniversary,
              divorce_date: mainSpouse.divorce_date,
            });

            // Merge cross-validation errors with existing errors
            if (crossErrors.primary.dob && !primaryDateErrors.dob) {
              primaryDateErrors.dob = crossErrors.primary.dob;
            }
            if (crossErrors.primary.dod && !primaryDateErrors.dod) {
              primaryDateErrors.dod = crossErrors.primary.dod;
            }
            if (crossErrors.primary.weddingAnniversary && !primaryDateErrors.weddingAnniversary) {
              primaryDateErrors.weddingAnniversary = crossErrors.primary.weddingAnniversary;
            }

            // Also update spouse errors from cross-validation
            setDateValidationErrors((prevErrors) => ({
              ...prevErrors,
              spouse: {
                ...prevErrors.spouse,
                ...crossErrors.spouse,
              },
            }));
          }

          // Cross-validate with all additional spouses (index 1 and beyond)
          spouses.slice(1).forEach((spouse, index) => {
            const crossErrors = validateCrossSpouseDates(updatedData, {
              dob: spouse.dob,
              dod: spouse.dod,
              weddingAnniversary:
                spouse.wedding_anniversary || spouse.weddingAnniversary,
              divorce_date: spouse.divorce_date,
            });

            // Merge cross-validation errors with existing primary errors (take the first error found)
            if (crossErrors.primary.dob && !primaryDateErrors.dob) {
              primaryDateErrors.dob = crossErrors.primary.dob;
            }
            if (crossErrors.primary.dod && !primaryDateErrors.dod) {
              primaryDateErrors.dod = crossErrors.primary.dod;
            }
            if (crossErrors.primary.weddingAnniversary && !primaryDateErrors.weddingAnniversary) {
              primaryDateErrors.weddingAnniversary = crossErrors.primary.weddingAnniversary;
            }

            // Update additional spouse errors from cross-validation
            setDateValidationErrors((prevErrors) => {
              const currentAdditionalSpouseErrors = prevErrors.additional_spouse || [];
              const updatedErrors = [...currentAdditionalSpouseErrors];

              // Ensure the array is long enough
              while (updatedErrors.length <= index) {
                updatedErrors.push({
                  dob: "",
                  dod: "",
                  wedding_anniversary: "",
                  divorce_date: "",
                });
              }

              // Merge cross-validation errors with existing errors
              updatedErrors[index] = {
                ...updatedErrors[index],
                ...(crossErrors.spouse.dob && !updatedErrors[index].dob
                  ? { dob: crossErrors.spouse.dob }
                  : {}),
                ...(crossErrors.spouse.dod && !updatedErrors[index].dod
                  ? { dod: crossErrors.spouse.dod }
                  : {}),
                ...(crossErrors.spouse.weddingAnniversary && !updatedErrors[index].wedding_anniversary
                  ? { wedding_anniversary: crossErrors.spouse.weddingAnniversary }
                  : {}),
                ...(crossErrors.spouse.divorce_date && !updatedErrors[index].divorce_date
                  ? { divorce_date: crossErrors.spouse.divorce_date }
                  : {}),
              };

              return {
                ...prevErrors,
                additional_spouse: updatedErrors,
              };
            });
          });
        }

        // Update validation errors
        setDateValidationErrors((prevErrors) => ({
          ...prevErrors,
          primary: {
            ...prevErrors.primary,
            ...primaryDateErrors,
          },
        }));

        return updatedData;
      });

      return; // Return early to prevent the state update below
    }

    // For non-date fields, update state normally
    if (name === "maritalStatus" && value === "3") {
      setGrandfatherData((prev) => ({
        ...prev,
        [name]: value,
        weddingAnniversary: "",
      }));
      setSpouses((prev) =>
        prev.map((spouse) => ({
          ...spouse,
          weddingAnniversary: "",
          wedding_anniversary: "",
          marriedYears: "",
          divorce: "",
          divorce_date: "",
        }))
      );
      setDateValidationErrors((prev) => ({
        ...prev,
        primary: { ...prev.primary, weddingAnniversary: "" },
        spouse: {
          ...prev.spouse,
          weddingAnniversary: "",
          divorce_date: "",
        },
      }));
      setSpouseValidationErrors((prev) => ({
        ...prev,
        divorce: "",
      }));
    } else {
      setGrandfatherData((prev) => ({ ...prev, [name]: value }));
    }

    // console.log("handleGrandfather change here")
    // console.log("name : ",name ,"value : ",value)

    // Reset spouse data if primary member is set to Single (maritalStatus === '1')
    if (name === "maritalStatus" && value === "1") {
      // Only reset if we are changing to Single - reset main spouse and clear additional spouses
      setSpouses([
        {
          id: "main-spouse",
          firstName: "",
          middleName: "",
          specialName: "",
          lastName: "",
          suffix: "",
          maidenName: "",
          nickName: "",
          gender: "",
          dob: "",
          stateOfBirth: "",
          currentStateResiding: "",
          aliveStatus: "",
          dod: "",
          ageOfDeath: "",
          currentAge: "",
          maritalStatus: "",
          weddingAnniversary: "",
          note: "",
          relation: spouseRelation,
          email: "",
          email_domain: "",
          email_custom_domain: "",
          phone: "",
          countryCode: "",
          phone_type: "",
          marriedYears: "",
        },
      ]);
      setOpenSpouseSections([]);
      // Close spouse section if it was open
      if (openSection === "spouse") {
        setOpenSection("primary");
      }
    } else if (name == "dod") {
      const age = calculateAge(grandfatherData?.dob, value);

      setGrandfatherData((prev) => ({ ...prev, ageOfDeath: age }));
    } else if (name == "dob") {
      const age = grandfatherData?.dod
        ? calculateAge(value, grandfatherData.dod)
        : "";
      const current = calculateAge(value);

      setGrandfatherData((prev) => ({
        ...prev,
        ageOfDeath: age,
        currentAge: current,
      }));
    }
  };


  const handleAddAnotherMarriage = (e) => {
    e.preventDefault();

    const newSpouseId = Date.now();
    console.log("Adding new spouse with ID:", newSpouseId);

    setSpouses((prev) => {
      const newSpouses = [
        ...prev,
        {
          id: newSpouseId,
          firstName: "",
          middleName: "",
          specialName: "",
          lastName: "",
          suffix: "",
          maidenName: "",
          nickName: "",
          gender: grandfatherData?.gender == 1 ? 2 : 1 || "",
          dob: "",
          stateOfBirth: "",
          currentStateResiding: "",
          aliveStatus: "",
          dod: "",
          ageOfDeath: "",
          currentAge: "",
          note: "",
          email: "",
          email_domain: "",
          email_custom_domain: "",
          phone: "",
          countryCode: "",
          phone_type: "",
          divorce: "",
          divorce_date: "",
          multiple_births_type: "",
          // relation: `${spouseRelation} (Marriage ${prev.length})`,
          // relation: grandmotherData?.relation
          relation: grandfatherData?.relation
            ? convertRelationByGender(grandfatherData?.relation, grandfatherData?.gender == 1 ? "2" : "1") + "-in-law"
            : "",
        },
      ];
      console.log("New spouses array:", newSpouses);
      return newSpouses;
    });

    setOpenSpouseSections((prev) => {
      const newOpenSections = [...prev, newSpouseId];
      console.log("New open sections:", newOpenSections);
      return newOpenSections;
    });
  };

  const handleAdditionalSpouseChange = (e, spouseId, index = null) => {
    const { name, value } = e.target;

    // Calculate index if not provided (for additional spouses)
    let spouseIndex = index;
    if (spouseIndex === null) {
      // Find the index in the current spouses array
      const spouseIndexInArray = spouses.findIndex((s) => s.id === spouseId);
      // Additional spouses are at index 1 and beyond, so their index in additional_spouse array is spouseIndexInArray - 1
      spouseIndex = spouseIndexInArray > 0 ? spouseIndexInArray - 1 : null;
    }

    // Handle text input fields - trim leading/trailing spaces
    const textFields = [
      "firstName",
      "middleName",
      "specialName",
      "lastName",
      "suffix",
      "maidenName",
      "note",
      "email",
    ];

    if (textFields.includes(name)) {
      // Trim the value on change (remove leading/trailing spaces)
      const trimmedValue = value.trimStart(); // Allow spaces in the middle, but trim leading spaces
      setSpouses((prev) =>
        prev.map((spouse) =>
          spouse.id === spouseId ? { ...spouse, [name]: trimmedValue } : spouse
        )
      );
      return;
    }

    // Handle date fields with validation
    if (
      name === "dob" ||
      name === "dod" ||
      name === "divorce_date" ||
      name === "weddingAnniversary" ||
      name === "wedding_anniversary"
    ) {
      // Mark date field and related fields as touched for cross-validation
      setTouchedFields((prev) => ({
        ...prev,
        additional_spouse: {
          ...prev.additional_spouse,
          [spouseIndex]: {
            ...prev.additional_spouse?.[spouseIndex],
            [name]: true,
            // Mark related fields as touched for cross-validation
            ...(name === "dob"
              ? { dod: true, wedding_anniversary: true, divorce_date: true }
              : {}),
            ...(name === "dod" ? { dob: true } : {}),
            ...(name === "wedding_anniversary" || name === "weddingAnniversary"
              ? { dob: true, divorce_date: true }
              : {}),
            ...(name === "divorce_date" ? { dob: true, wedding_anniversary: true } : {}),
          },
        },
      }));

      // Update state and validate dates with updated value
      setSpouses((prev) => {
        let updatedSpouses = prev.map((spouse) =>
          spouse.id === spouseId ? { ...spouse, [name]: value } : spouse
        );
        const updatedSpouse = updatedSpouses.find((s) => s.id === spouseId);

        if (updatedSpouse && spouseIndex !== null) {
          const additionalSpouseDateErrors = validateAdditionalSpouseDates({
            dob: updatedSpouse.dob,
            dod: updatedSpouse.dod,
            weddingAnniversary:
              updatedSpouse.wedding_anniversary ||
              updatedSpouse.weddingAnniversary,
            divorce_date: updatedSpouse.divorce_date,
          });

          // Cross-validate with primary member dates
          const isSingle = grandfatherData.maritalStatus === "1";
          if (!isSingle) {
            const crossErrors = validateCrossSpouseDates(
              {
                dob: grandfatherData.dob,
                dod: grandfatherData.dod,
                weddingAnniversary: grandfatherData.weddingAnniversary,
              },
              {
                dob: updatedSpouse.dob,
                dod: updatedSpouse.dod,
                weddingAnniversary:
                  updatedSpouse.wedding_anniversary ||
                  updatedSpouse.weddingAnniversary,
                divorce_date: updatedSpouse.divorce_date,
              }
            );

            // Merge cross-validation errors with existing additional spouse errors
            if (crossErrors.spouse.dob && !additionalSpouseDateErrors.dob) {
              additionalSpouseDateErrors.dob = crossErrors.spouse.dob;
            }
            if (crossErrors.spouse.dod && !additionalSpouseDateErrors.dod) {
              additionalSpouseDateErrors.dod = crossErrors.spouse.dod;
            }
            if (crossErrors.spouse.weddingAnniversary && !additionalSpouseDateErrors.weddingAnniversary) {
              additionalSpouseDateErrors.weddingAnniversary = crossErrors.spouse.weddingAnniversary;
            }
            if (crossErrors.spouse.divorce_date && !additionalSpouseDateErrors.divorce_date) {
              additionalSpouseDateErrors.divorce_date = crossErrors.spouse.divorce_date;
            }

            // Also update primary member errors from cross-validation
            setDateValidationErrors((prevErrors) => ({
              ...prevErrors,
              primary: {
                ...prevErrors.primary,
                ...crossErrors.primary,
              },
            }));
          }

          setDateValidationErrors((prev) => {
            // Ensure additional_spouse array exists and has enough elements
            const currentAdditionalSpouseErrors = prev.additional_spouse || [];
            const updatedErrors = [...currentAdditionalSpouseErrors];

            // Ensure the array is long enough
            while (updatedErrors.length <= spouseIndex) {
              updatedErrors.push({
                dob: "",
                dod: "",
                wedding_anniversary: "",
                divorce_date: "",
              });
            }

            updatedErrors[spouseIndex] = {
              ...updatedErrors[spouseIndex],
              ...additionalSpouseDateErrors,
              // Map weddingAnniversary to wedding_anniversary for consistency
              wedding_anniversary:
                additionalSpouseDateErrors.weddingAnniversary || "",
            };

            return {
              ...prev,
              additional_spouse: updatedErrors,
            };
          });
        }

        const weddingDate =
          updatedSpouse.wedding_anniversary || updatedSpouse.weddingAnniversary;
        const marriedYears = calcMarriedYears(weddingDate, {
          divorce: updatedSpouse.divorce,
          divorce_date: updatedSpouse.divorce_date,
          aliveStatus: updatedSpouse.aliveStatus,
          dod: updatedSpouse.dod,
          descendantAliveStatus: grandfatherData.aliveStatus,
          descendantDod: grandfatherData.dod,
        });
        updatedSpouses = updatedSpouses.map((spouse) =>
          spouse.id === spouseId ? { ...spouse, marriedYears } : spouse
        );

        return updatedSpouses;
      });

      return; // Return early to prevent the state update below
    }

    // For non-date fields, update state normally
    setSpouses((prev) => {
      const updatedSpouses = prev.map((spouse) =>
        spouse.id === spouseId ? { ...spouse, [name]: value } : spouse
      );

      const updatedSpouse = updatedSpouses.find((s) => s.id === spouseId);
      if (updatedSpouse) {
        const weddingDate =
          updatedSpouse.wedding_anniversary || updatedSpouse.weddingAnniversary;
        const marriedYears = calcMarriedYears(weddingDate, {
          divorce: updatedSpouse.divorce,
          divorce_date: updatedSpouse.divorce_date,
          aliveStatus: updatedSpouse.aliveStatus,
          dod: updatedSpouse.dod,
          descendantAliveStatus: grandfatherData.aliveStatus,
          descendantDod: grandfatherData.dod,
        });
        return updatedSpouses.map((spouse) =>
          spouse.id === spouseId ? { ...spouse, marriedYears } : spouse
        );
      }

      return updatedSpouses;
    });

    // Clear divorce errors from additionalSpouseErrors when divorce field changes
    if (name === "divorce") {
      setAdditionalSpouseErrors((prev) => {
        const updated = { ...prev };
        if (updated[spouseId]) {
          delete updated[spouseId].divorce;
          if (Object.keys(updated[spouseId]).length === 0) {
            delete updated[spouseId];
          }
        }
        return updated;
      });

      // If divorce is set to "No" (2), clear divorce_date errors and married years
      if (value === "2" || value === 2) {
        if (spouseIndex !== null) {
          setDateValidationErrors((prev) => ({
            ...prev,
            additional_spouse: prev.additional_spouse?.map((spouseErrors, idx) =>
              idx === spouseIndex ? { ...spouseErrors, divorce_date: "" } : spouseErrors
            ) || [],
          }));
        }
        setSpouses((prev) => {
          const updatedSpouses = prev.map((spouse) =>
            spouse.id === spouseId ? { ...spouse, [name]: value } : spouse
          );
          const updatedSpouse = updatedSpouses.find((s) => s.id === spouseId);
          if (!updatedSpouse) return updatedSpouses;
          const weddingDate =
            updatedSpouse.wedding_anniversary || updatedSpouse.weddingAnniversary;
          const marriedYears = calcMarriedYears(weddingDate, {
            divorce: updatedSpouse.divorce,
            divorce_date: updatedSpouse.divorce_date,
            aliveStatus: updatedSpouse.aliveStatus,
            dod: updatedSpouse.dod,
            descendantAliveStatus: grandfatherData.aliveStatus,
            descendantDod: grandfatherData.dod,
          });
          return updatedSpouses.map((spouse) =>
            spouse.id === spouseId ? { ...spouse, marriedYears } : spouse
          );
        });
      } else {
        setSpouses((prev) => {
          const updatedSpouses = prev.map((spouse) =>
            spouse.id === spouseId ? { ...spouse, [name]: value } : spouse
          );
          const updatedSpouse = updatedSpouses.find((s) => s.id === spouseId);
          if (!updatedSpouse) return updatedSpouses;
          const weddingDate =
            updatedSpouse.wedding_anniversary || updatedSpouse.weddingAnniversary;
          const marriedYears = calcMarriedYears(weddingDate, {
            divorce: updatedSpouse.divorce,
            divorce_date: updatedSpouse.divorce_date,
            aliveStatus: updatedSpouse.aliveStatus,
            dod: updatedSpouse.dod,
            descendantAliveStatus: grandfatherData.aliveStatus,
            descendantDod: grandfatherData.dod,
          });
          return updatedSpouses.map((spouse) =>
            spouse.id === spouseId ? { ...spouse, marriedYears } : spouse
          );
        });
      }
    }
  };

  const removeAdditionalSpouse = (spouseId) => {
    setSpouses((prev) => prev.filter((spouse) => spouse.id !== spouseId));
    setOpenSpouseSections((prev) => prev.filter((id) => id !== spouseId));
  };

  const toggleSpouseSection = (spouseId) => {
    console.log("Toggling spouse section:", spouseId);
    console.log("Current open sections:", openSpouseSections);

    setOpenSpouseSections((prev) => {
      if (prev.includes(spouseId)) {
        // If already open, close it
        const newSections = prev.filter((id) => id !== spouseId);
        console.log("Closing section, new sections:", newSections);
        return newSections;
      } else {
        // If closed, open it
        const newSections = [...prev, spouseId];
        console.log("Opening section, new sections:", newSections);
        return newSections;
      }
    });
  };

  const handleGrandmotherChange = (e) => {
    const { name, value } = e.target;

    // Handle text input fields - trim leading/trailing spaces
    const textFields = [
      "firstName",
      "middleName",
      "specialName",
      "lastName",
      "suffix",
      "maidenName",
      "note",
      "email",
      "phone_extension",
    ];

    if (textFields.includes(name)) {
      // Trim the value on change (remove leading/trailing spaces)
      const trimmedValue = value.trimStart(); // Allow spaces in the middle, but trim leading spaces
      setSpouses((prev) => {
        const newSpouses = [...prev];
        newSpouses[0] = { ...newSpouses[0], [name]: trimmedValue };
        return newSpouses;
      });
      return;
    }

    // Handle date fields with validation
    if (
      name === "dob" ||
      name === "dod" ||
      name === "weddingAnniversary" ||
      name == "divorce_date"
    ) {
      setTouchedFields((prev) => ({
        ...prev,
        spouse: {
          ...prev.spouse,
          [name]: true,
          // Mark related fields as touched for cross-validation
          ...(name === "dob"
            ? { dod: true, weddingAnniversary: true, divorce_date: true }
            : {}),
          ...(name === "dod" ? { dob: true } : {}),
          ...(name === "weddingAnniversary" ? { dob: true, divorce_date: true } : {}),
          ...(name === "divorce_date" ? { dob: true, weddingAnniversary: true } : {}),
        },
      }));

      // Update state and validate dates with updated value
      setSpouses((prev) => {
        const newSpouses = [...prev];
        newSpouses[0] = { ...newSpouses[0], [name]: value };
        const updatedData = newSpouses[0];

        const spouseDateErrors = validateSpouseDates({
          dob: updatedData.dob,
          dod: updatedData.dod,
          weddingAnniversary: updatedData.weddingAnniversary,
          divorce_date: updatedData.divorce_date,
        });

        // Cross-validate with primary member dates
        const isSingle = grandfatherData.maritalStatus === "1";
        if (!isSingle) {
          const crossErrors = validateCrossSpouseDates(
            {
              dob: grandfatherData.dob,
              dod: grandfatherData.dod,
              weddingAnniversary: grandfatherData.weddingAnniversary,
            },
            {
              dob: updatedData.dob,
              dod: updatedData.dod,
              weddingAnniversary: updatedData.weddingAnniversary,
              divorce_date: updatedData.divorce_date,
            }
          );

          // Merge cross-validation errors with existing spouse errors
          if (crossErrors.spouse.dob && !spouseDateErrors.dob) {
            spouseDateErrors.dob = crossErrors.spouse.dob;
          }
          if (crossErrors.spouse.dod && !spouseDateErrors.dod) {
            spouseDateErrors.dod = crossErrors.spouse.dod;
          }
          if (crossErrors.spouse.weddingAnniversary && !spouseDateErrors.weddingAnniversary) {
            spouseDateErrors.weddingAnniversary = crossErrors.spouse.weddingAnniversary;
          }
          if (crossErrors.spouse.divorce_date && !spouseDateErrors.divorce_date) {
            spouseDateErrors.divorce_date = crossErrors.spouse.divorce_date;
          }

          // Also update primary member errors from cross-validation
          setDateValidationErrors((prev) => ({
            ...prev,
            primary: {
              ...prev.primary,
              ...crossErrors.primary,
            },
          }));
        }

        setDateValidationErrors((prev) => ({
          ...prev,
          spouse: {
            ...prev.spouse,
            ...spouseDateErrors,
          },
        }));

        const marriedYears = calcMarriedYears(
          updatedData.weddingAnniversary || updatedData.wedding_anniversary,
          {
            divorce: updatedData.divorce,
            divorce_date: updatedData.divorce_date,
            aliveStatus: updatedData.aliveStatus,
            dod: updatedData.dod,
            descendantAliveStatus: grandfatherData.aliveStatus,
            descendantDod: grandfatherData.dod,
          }
        );
        newSpouses[0] = { ...newSpouses[0], marriedYears };

        return newSpouses;
      });

      return; // Return early to prevent the state update below
    }

    // For non-date fields, update state normally
    setSpouses((prev) => {
      const newSpouses = [...prev];
      newSpouses[0] = { ...newSpouses[0], [name]: value };

      const updatedData = newSpouses[0];
      const marriedYears = calcMarriedYears(
        updatedData.weddingAnniversary || updatedData.wedding_anniversary,
        {
          divorce: updatedData.divorce,
          divorce_date: updatedData.divorce_date,
          aliveStatus: updatedData.aliveStatus,
          dod: updatedData.dod,
          descendantAliveStatus: grandfatherData.aliveStatus,
          descendantDod: grandfatherData.dod,
        }
      );
      newSpouses[0] = { ...newSpouses[0], marriedYears };

      return newSpouses;
    });

    if (name == "dod") {
      const age = calculateAge(spouses[0]?.dob, value);
      setSpouses((prev) => {
        const newSpouses = [...prev];
        const updated = { ...newSpouses[0], dod: value, ageOfDeath: age };
        const marriedYears = calcMarriedYears(
          updated.weddingAnniversary || updated.wedding_anniversary,
          {
            divorce: updated.divorce,
            divorce_date: updated.divorce_date,
            aliveStatus: updated.aliveStatus,
            dod: value,
            descendantAliveStatus: grandfatherData.aliveStatus,
            descendantDod: grandfatherData.dod,
          }
        );
        newSpouses[0] = { ...updated, marriedYears };
        return newSpouses;
      });
    } else if (name == "dob") {
      const currentSpouse = spouses[0];
      const age = currentSpouse?.dod
        ? calculateAge(value, currentSpouse.dod)
        : "";
      const current = calculateAge(value);

      setSpouses((prev) => {
        const newSpouses = [...prev];
        newSpouses[0] = {
          ...newSpouses[0],
          ageOfDeath: age,
          currentAge: current,
        };
        return newSpouses;
      });
    } else if (name === "divorce") {
      // Clear or calculate married years when divorce field changes
      setSpouses((prev) => {
        const newSpouses = [...prev];
        newSpouses[0] = { ...newSpouses[0], [name]: value };
        
        const updatedData = newSpouses[0];
        const marriedYears = calcMarriedYears(
          updatedData.weddingAnniversary || updatedData.wedding_anniversary,
          {
            divorce: updatedData.divorce,
            divorce_date: updatedData.divorce_date,
            aliveStatus: updatedData.aliveStatus,
            dod: updatedData.dod,
            descendantAliveStatus: grandfatherData.aliveStatus,
            descendantDod: grandfatherData.dod,
          }
        );
        newSpouses[0] = { ...newSpouses[0], marriedYears };
        
        return newSpouses;
      });
      
      // Clear divorce validation error when value changes
      if (value) {
        setSpouseValidationErrors((prev) => ({
          ...prev,
          divorce: "",
        }));
      }
    }
  };

  const toggleSection = (section) => {
    setOpenSection((prev) => (prev === section ? "" : section));
    // Close all spouse sections when toggling primary section
    if (section === "spouse") {
      setOpenSpouseSections([]);
    }
  };

  // Validate date fields
  const validateDates = () => {
    return validateDatesUtils({
      grandfatherData,
      grandmotherData,
      spouses,
      parentMember,
      setDateValidationErrors,
      isEditMode,
      editMemberData,
    });
  };

  const handleSubmit = async (e) => {
    await handleFormSubmit({
      e,
      // State setters
      setFormSubmitted,
      setDateValidationErrors,
      setImageValidationError,
      setSpouseImageValidationError,
      setSpouseValidationErrors,
      setAdditionalSpouseImageErrors,
      setAdditionalSpouseErrors,
      setTouchedFields,
      setFieldValidationErrors,
      setLoading,
      // State values
      hasEmailConflict,
      grandfatherData,
      grandmotherData,
      spouses,
      uploadedImages,
      dateValidationErrors,
      isEditMode,
      editMemberData,
      parentMember,
      parentGeneration,
      currentGeneration,
      parentChildren: parentChildrenData.length > 0 ? parentChildrenData : (parentMember?.children || []),
      familyTreeId,
      rootHeadData,
      selectedProfileColor,
      // Section accordion setters (used to reveal collapsed sections on error)
      setOpenSection,
      setOpenSpouseSections,
      // Functions
      validateDates,
      // Callbacks
      onNext,
      onClose,
    });
  };

  const handleAdditionalSpousePhoneChange = (value, country, spouseId) => {
    const dialCode = `+${country.dialCode}`;
    const trimmed = value.trim();
    const match = trimmed.match(/^(\+\d{1,3})\s*(.*)$/);

    const extractedCountryCode = match ? match[1] : dialCode;
    const rawNational = match ? match[2] || "" : trimmed;
    const phoneNumber = limitNationalPhoneDigits(
      rawNational.replace(/\D/g, "") || trimmed.replace(/\D/g, ""),
      country.dialCode
    );
    const phoneForPicker = combinePhoneNumber(
      phoneNumber,
      extractedCountryCode
    );

    setSpouses((prev) =>
      prev.map((spouse) =>
        spouse.id === spouseId
          ? {
              ...spouse,
              phone: phoneForPicker,
              countryCode: extractedCountryCode,
            }
          : spouse
      )
    );
  };

  const handleFileChange = (e, type) => {
    const files = Array.from(e.target.files);
    console.log("image file : ", files, "type : ", type);
    if (files.length == 0) return;

    const newImages = files.map((file, index) => {
      // // Check what types are already uploaded

      const existingImage = uploadedImages.find(
        (img) => img.type === type && !img.file
      );

      return {
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        url: URL.createObjectURL(file),
        unique_image_id: existingImage ? existingImage.unique_image_id : null, // Preserve the unique ID if replacing
        file,
        type,
      };
    });
    //old code allowing multiple img upload
    // setUploadedImages((prev) => [...prev, ...newImages].slice(0, 10));

    //new code replacing img of same type upon reupload
    setUploadedImages((prev) => {
      // Filter out existing image of the same type
      const filteredImages = prev.filter((img) => img.type !== type);
      console.log("filtered images : ", filteredImages);
      // Add new image(s) for this type

      // return [...filteredImages, ...newImages].slice(0, 10); // Still limit total images
      const updatedImages = [...filteredImages, ...newImages]; // Still limit total images

      // Clear image validation errors when image is uploaded
      if (type === "spouse-main-spouse" || type === "spouse") {
        setSpouseImageValidationError(false);
        setSpouseValidationErrors((prev) => ({
          ...prev,
          image: "",
        }));
      } else if (type.startsWith("spouse-")) {
        // Additional spouse image uploaded - extract spouse ID
        const spouseId = type.replace("spouse-", "");
        setAdditionalSpouseImageErrors((prev) => {
          const updated = { ...prev };
          delete updated[spouseId];
          return updated;
        });
      }

      return updatedImages;
    });

    console.log("new images : ", newImages);

    if (type == "main") {
      setImageValidationError(false);
    }
    if (type == "spouse-main-spouse") {
      setSpouseImageValidationError(false);
    } // Limit to 2 images (main + spouse)
    e.target.value = null;
  };

  const removeImage = (id) => {
    const imageToRemove = uploadedImages.find((img) => img.id === id);
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.url);
    }

    // If it's an existing image (has unique_image_id), we need to mark it for deletion
    if (imageToRemove.unique_image_id && !imageToRemove.file) {
      // You might want to track deleted images to inform the backend
      console.log(
        `Marking existing image ${imageToRemove.unique_image_id} for deletion`
      );
    }

    setUploadedImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handlePhoneChange = (value, country, isGrandmother = false) => {
    const dialCode = `+${country.dialCode}`; // e.g. "+1"
    const trimmed = value.trim();

    setPhoneValidationError(false);

    // Try to detect if the input starts with a +countryCode
    const match = trimmed.match(/^(\+\d{1,3})\s*(.*)$/);

    const extractedCountryCode = match ? match[1] : dialCode;
    const rawNational = match ? match[2] || "" : trimmed;
    const phoneNumber = limitNationalPhoneDigits(
      rawNational.replace(/\D/g, "") || trimmed.replace(/\D/g, ""),
      country.dialCode
    );
    const phoneForPicker = combinePhoneNumber(
      phoneNumber,
      extractedCountryCode
    );

    const updatedData = (prev) => ({
      ...prev,
      phone: phoneForPicker,
      countryCode: extractedCountryCode,
    });

    if (isGrandmother) {
      setSpouses((prev) => {
        const newSpouses = [...prev];
        newSpouses[0] = { ...newSpouses[0], ...updatedData(newSpouses[0]) };
        return newSpouses;
      });
    } else {
      setGrandfatherData((prev) => updatedData(prev));
    }
  };

  // Flag for any email conflicts returned from the API
  const hasEmailConflict =
    !!fieldValidationErrors.email ||
    !!spouseValidationErrors.email ||
    Object.values(additionalSpouseErrors).some(
      (err) => err && err.email && err.email.length > 0
    );

  // Email validation function to check if email exists in family tree
  const checkEmailExists = async (email, emailType = "primary") => {
    await checkEmailExistsUtils(email, emailType, {
      familyTreeId,
      parentMember,
      setFieldValidationErrors,
      setSpouseValidationErrors,
      setAdditionalSpouseErrors,
    });
  };

  // const showSpouseSection =
  //   grandfatherData.maritalStatus !== "" &&
  //   Number(grandfatherData.maritalStatus) !== 1;
  const showSpouseSection =
    (grandfatherData.maritalStatus !== "" &&
      Number(grandfatherData.maritalStatus) !== 1) ||
    (isEditMode && spouses.length > 0 && (spouses[0]?.firstName || spouses[0]?.lastName || spouses[0]?.email));
  

  // console.log("data in add/edit modal", grandfatherData);
  // console.log("granny data in add/edit modal", grandmotherData);
  // console.log("uploaded images : ", uploadedImages);

  // Handle color change
  const handleColorChange = (newColorData) => {
    console.log("Color changed to:", newColorData);

    // Update the rootHeadData with new color
    setRootHeadData2((prev) => ({
      ...prev,
      selectedColor: newColorData.selectedColor,
      colorName: newColorData.colorName,
    }));

    setSelectedProfileColor({
      selectedColor: newColorData.selectedColor,
      colorName: newColorData.colorName,
    });

    // Close the color change modal
    setShowColorChangeModal(false);

    // Optional: Show success message
    // toast.success("Color updated successfully!");
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="popup-card addmember-card"
      overlayClassName="popup-overlay addmember-overlay"
      closeTimeoutMS={200}
      ariaHideApp={false}
    >
      <Modal
        isOpen={showDeleteModal}
        onRequestClose={() => setShowDeleteModal(false)}
        overlayClassName="popup-overlay invite-overlay"
        portalClassName="event-moodal popup-card  delete-confirmation"
        ariaHideApp={false}
      // className="delete-confirmation-card"
      >
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={handleDeleteModalClose}
          itemDetails={selectedItem}
          itemType={selectedItem?.type || "profile"}
          ariaHideApp={false}
          onSuccess={(deletedId) => {
            // Handle successful deletion (update state, etc.)
            // Backend should recalculate multiple birth groups when a member is deleted:
            // downgrade or dissolve group (e.g. triplet → twin), reassign birth order.
            console.log(`Item ${deletedId} deleted successfully`);
            
            // Check if deleted member is root (generation_level === 1 or generation === 1)
            const isRootMember = selectedItem?.generation_level === 1 || 
                                 selectedItem?.generation === 1 ||
                                 (!selectedItem?.parentId && !selectedItem?.related_member_id);
            
            if (isRootMember) {
              // Remove family_tree_id from localStorage when root member is deleted
              try {
                if (getFamilyTreeIdFromStorage()) {
                  clearFamilyTreeIdsFromStorage();
                  console.log("✅ Cleared family tree id from storage after root member deletion");
                  
                  // Refresh the page to clear the tree view
                  setTimeout(() => {
                    window.location.reload();
                  }, 500); // Small delay to ensure localStorage is updated
                  return; // Don't call onNext() if we're refreshing
                }
              } catch (error) {
                console.error("Error removing family_tree_id from localStorage:", error);
              }
            }
            
            // onClose();
            onNext();
          }}
        />
      </Modal>

      <RootHeadModal
        isOpen={showColorChangeModal}
        onClose={() => setShowColorChangeModal(false)}
        onNext={handleColorChange}
        parentMember={editMemberData}
        // parentMember={parentMember}
        isEditMode={isEditMode}
        selectedProfileColor={selectedProfileColor}
        setSelectedProfileColor={setSelectedProfileColor}
        rootHeadData={{
          ...rootHeadData2,
          // Pass the current member data for context
          selectedHeadId: editMemberData?.id,
          selectedHeadName:
            editMemberData?.relation || rootHeadData?.selectedHeadName,
          generation: editMemberData?.generation || rootHeadData?.generation,
        }}
      />
      <form onSubmit={handleSubmit}>
        {/* ===== Header ===== */}
        <div
          className="popup-header addmember-header"
          style={{
            backgroundColor: rootHeadData?.selectedColor || "#A6A6B5",
          }}
        >
          <div className="header-left">
            <div className="">
              <img src={window.getPublicUrl("/assets/images/add-member.svg")} alt="user" />
            </div>
            <div>
              {isEditMode ? (
                <h2>
                  {`Edit Family Member Info for ${editMemberData?.firstName || editMemberData?.first_name || editMemberData?.userData?.first_name || ""} ${editMemberData?.lastName || editMemberData?.last_name || editMemberData?.userData?.last_name || ""}${editMemberData?.suffix || editMemberData?.userData?.suffix ? `, ${editMemberData?.suffix || editMemberData?.userData?.suffix}` : ""}`}
                </h2>
              ) : parentMember ? (
                <>
                  <h2>
                    Add child to{" "}
                    <strong>
                      {(() => {
                        // Helper function to format suffix with period if needed
                        const formatSuffix = (suffix) => {
                          if (!suffix) return "";
                          const trimmedSuffix = suffix.trim();
                          // If suffix already ends with a period, return as-is
                          if (trimmedSuffix.endsWith(".")) return trimmedSuffix;
                          // Common abbreviation suffixes that should have periods
                          const abbreviationSuffixes = ["Sr", "Jr", "Esq", "PhD", "MD", "CPA", "DDS", "DVM"];
                          const upperSuffix = trimmedSuffix.toUpperCase();
                          // Check if it's a common abbreviation (case-insensitive)
                          if (abbreviationSuffixes.some(abbr => upperSuffix === abbr.toUpperCase())) {
                            return trimmedSuffix + ".";
                          }
                          // For numeric suffixes (III, IV, etc.) or other suffixes, return as-is
                          return trimmedSuffix;
                        };
                        
                        // For generation 2, show full name with last name
                        // For other generations, show only first name + special initial + suffix (no last name)
                        if (currentGeneration === 2) {
                          // Format parent name with suffix - prioritize formatted version if we have name parts
                          const hasParentNameParts = (parentMember.firstName || parentMember.lastName);
                          const parentSuffix = formatSuffix(parentMember.suffix || "");
                          const parentName = hasParentNameParts
                            ? formatNameWithSuffix(
                                parentMember.firstName || "",
                                parentMember.middleName || "",
                                parentMember.lastName || "",
                                parentSuffix
                              )
                            : (parentMember.fullName || "");
                          
                          // Format spouse name with suffix - prioritize formatted version if we have name parts
                          const hasSpouseNameParts = (parentMember.spouseFirstName || parentMember.spouse_first_name || parentMember.spouseLastName || parentMember.spouse_last_name);
                          const spouseSuffix = formatSuffix(parentMember.spouseSuffix || parentMember.spouse_suffix || "");
                          const spouseName = hasSpouseNameParts
                            ? formatNameWithSuffix(
                                parentMember.spouseFirstName || parentMember.spouse_first_name || "",
                                parentMember.spouseMiddleName || parentMember.spouse_middle_name || "",
                                parentMember.spouseLastName || parentMember.spouse_last_name || "",
                                spouseSuffix
                              )
                            : (parentMember.spouseFullName || parentMember.spouse_full_name || "");
                          
                          if (spouseName) {
                            return `${parentName} & ${spouseName}`;
                          }
                          return parentName;
                        } else {
                          // For other generations: only first name + special initial + suffix (no last name, no spouse)
                          const parentFirstName = parentMember.firstName || parentMember.first_name || "";
                          const parentSpecialName = parentMember.specialName || parentMember.special_name || "";
                          const parentSuffix = formatSuffix(parentMember.suffix || "");
                          
                          // Build parent name parts (only first name and special name)
                          const parentNameParts = [];
                          if (parentFirstName) {
                            parentNameParts.push(parentFirstName);
                          }
                          if (parentSpecialName) {
                            parentNameParts.push(parentSpecialName);
                          }
                          
                          const parentBaseName = parentNameParts.join(' ');
                          
                          // Add suffix with comma and period if it exists
                          let parentName = '';
                          if (parentSuffix) {
                            const suffixWithPeriod = parentSuffix.trim().endsWith('.') ? parentSuffix.trim() : `${parentSuffix.trim()}.`;
                            parentName = parentBaseName ? `${parentBaseName}, ${suffixWithPeriod}` : suffixWithPeriod;
                          } else {
                            parentName = parentBaseName || parentFirstName || (parentMember.fullName?.split(' ')[0] || parentMember.full_name?.split(' ')[0] || "");
                          }
                          
                          // For other generations, only return parent name (no spouse)
                          return parentName;
                        }
                      })()}
                    </strong>
                  </h2>
                  <p>
                    Enter child member details for printing and genealogy hierarchy.
                  </p>
                </>
              ) : (
                <>
                  <h2>Add Family Member</h2>
                  <p>
                    Enter family member details for printing and genealogy
                    hierarchy. Layout has been automatically selected based on
                    your prior choice.
                  </p>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* ===== Scrollable Content ===== */}
        <div className="popup-body addmember-body">
          {/* Rest of your existing JSX remains the same */}
          {/* Primary Member Section */}
          <div className="addmember-body-scroll">
            <MainMemberSection
              toggleSection={toggleSection}
              primaryLabel={primaryLabel}
              openSection={openSection}
              uploadedImages={uploadedImages}
              imageValidationError={imageValidationError}
              removeImage={removeImage}
              handleFileChange={handleFileChange}
              sortedGenderOptions={sortedGenderOptions}
              grandfatherData={grandfatherData}
              handleGrandfatherChange={handleGrandfatherChange}
              setFieldValidationErrors={setFieldValidationErrors}
              fieldValidationErrors={fieldValidationErrors}
              touchedFields={touchedFields}
              setTouchedFields={setTouchedFields}
              formSubmitted={formSubmitted}
              isRootGeneration={isRootGeneration}
              rootHeadData={rootHeadData}
              handleAddAnotherMarriage={handleAddAnotherMarriage}
              CustomDatePicker={CustomDatePicker}
              dateValidationErrors={dateValidationErrors}
              sortedStates={sortedStates}
              sortedMaritalStatusOptions={sortedMaritalStatusOptions}
              sortedLivingDeceasedOptions={sortedLivingDeceasedOptions}
              multipleBirthsOptions={multipleBirthsOptions}
              sortedCellTypeOptions={sortedCellTypeOptions}
              emailCheckTimeoutRef={emailCheckTimeoutRef}
              checkEmailExists={checkEmailExists}
              handlePhoneChange={handlePhoneChange}
              PhoneInput={PhoneInput}
              isEditMode={isEditMode}
              parentMember={parentMember}
              editMemberData={editMemberData}
              setGrandfatherData={setGrandfatherData}
              primaryRelation={primaryRelation}
              primaryRelationName={primaryRelationName}
              DropdownSelect={DropdownSelect}
              currentGeneration={currentGeneration}
              parentChildren={parentChildrenData.length > 0 ? parentChildrenData : (parentMember?.children || [])}
            />

            <MainSpouseSection
              handleFileChange={handleFileChange}
              DropdownSelect={DropdownSelect}
              sortedGenderOptions={sortedGenderOptions}
              sortedMaritalStatusOptions={sortedMaritalStatusOptions}
              sortedCellTypeOptions={sortedCellTypeOptions}
              grandmotherData={grandmotherData}
              handleGrandmotherChange={handleGrandmotherChange}
              setSpouseValidationErrors={setSpouseValidationErrors}
              spouseValidationErrors={spouseValidationErrors}
              touchedFields={touchedFields}
              formSubmitted={formSubmitted}
              setTouchedFields={setTouchedFields}
              grandfatherData={grandfatherData}
              CustomDatePicker={CustomDatePicker}
              dateValidationErrors={dateValidationErrors}
              sortedStates={sortedStates}
              sortedLivingDeceasedOptions={sortedLivingDeceasedOptions}
              emailCheckTimeoutRef={emailCheckTimeoutRef}
              checkEmailExists={checkEmailExists}
              handlePhoneChange={handlePhoneChange}
              PhoneInput={PhoneInput}
              sortedDivorceOptions={sortedDivorceOptions}
              showSpouseSection={showSpouseSection}
              toggleSpouseSection={toggleSpouseSection}
              openSpouseSections={openSpouseSections}
              uploadedImages={uploadedImages}
              removeImage={removeImage}
              spouseImageValidationError={spouseImageValidationError}
              setSpouses={setSpouses}
              isEditMode={isEditMode}
              editMemberData={editMemberData}
            />

            <AdditionalSpouseSection
              spouses={spouses}
              toggleSpouseSection={toggleSpouseSection}
              removeAdditionalSpouse={removeAdditionalSpouse}
              openSpouseSections={openSpouseSections}
              uploadedImages={uploadedImages}
              removeImage={removeImage}
              handleFileChange={handleFileChange}
              additionalSpouseImageErrors={additionalSpouseImageErrors}
              DropdownSelect={DropdownSelect}
              sortedGenderOptions={sortedGenderOptions}
              handleAdditionalSpouseChange={handleAdditionalSpouseChange}
              setAdditionalSpouseErrors={setAdditionalSpouseErrors}
              additionalSpouseErrors={additionalSpouseErrors}
              touchedFields={touchedFields}
              formSubmitted={formSubmitted}
              setTouchedFields={setTouchedFields}
              CustomDatePicker={CustomDatePicker}
              dateValidationErrors={dateValidationErrors}
              sortedStates={sortedStates}
              sortedLivingDeceasedOptions={sortedLivingDeceasedOptions}
              sortedDivorceOptions={sortedDivorceOptions}
              emailCheckTimeoutRef={emailCheckTimeoutRef}
              checkEmailExists={checkEmailExists}
              handlePhoneChange={handlePhoneChange}
              PhoneInput={PhoneInput}
              sortedCellTypeOptions={sortedCellTypeOptions}
              handleAdditionalSpousePhoneChange={handleAdditionalSpousePhoneChange}
              grandfatherData={grandfatherData}
              setSpouseValidationErrors={setSpouseValidationErrors}
              isEditMode={isEditMode}
            />

            {isEditMode && editMemberData?.generation <= 2 && isAdmin && (
              <div className="popup-actions addmember-actions delete-btn">

                <div
                  className="btn border-0 bg-none"
                  onClick={() => setShowColorChangeModal(true)}
                >
                  <img
                    src={window.getPublicUrl("/assets/images/color-palette.png")}
                    alt="Member"
                    style={{}}
                  />
                  <p className="mb-0">Change color</p>
                </div>
              </div>
            )}
          </div>
          {/* ===== Buttons ===== */}
          <div className="popup-actions addmember-actions addmember-actions--row">
            <button
              type="button"
              className="btn-outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            {/* Delete hidden: descendants are never deleted.
            {isEditMode && isAdmin ? (
              <button
                type="button"
                className="btn delete-profile-btn"
                onClick={(e) => openDeleteModal(editMemberData, "profile")}
              >
                Delete
              </button>
            ) : (
              ""
            )} */}
            <button
              type="submit"
              className="btn submit-btn"
              disabled={loading || hasEmailConflict}
              style={{
                backgroundColor: rootHeadData?.selectedColor || "#A6A6B5",
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isEditMode ? (
                "Update"
              ) : (
                "Next"
              )}
            </button>
          </div>
        </div>
      </form>
    </ReactModal>
  );
};

export default AddMemberModal;
